import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpDown,
  Star,
  ChevronDown,
  Info,
  Check,
  Plus,
  BookOpenCheck,
  Sparkles,
  X,
  Users,
  Award,
} from "lucide-react";
import { fetchLenderRates, fetchVerticalProviders, recordClick, getInsurancePremiumByState } from "../lib/api";
import {
  DETAILS_GLANCE_KEYS,
  detailLabel,
  fmtDetail,
} from "../lib/providerDetails";
import { useRevalidateOnCacheBump } from "../lib/cacheVersion";
import AffiliateDisclosure from "./AffiliateDisclosure";
import MarketplaceStrip from "./MarketplaceStrip";
import MethodologyBadge, { getMethodologyTarget } from "./MethodologyBadge";
import CarrierLogo from "./CarrierLogo";
import AmBestBadge from "./AmBestBadge";
import TrustBadgeStack from "./TrustBadgeStack";
// Iter-276.A (audit Top-5 #5) — Relative-time stamp ("Rates updated 3
// days ago") rendered inside the sticky sort bar so the freshness
// signal stays in the visitor's eye while they scan rows. Closes the
// "is this data current?" trust gap vs NerdWallet/Bankrate.
import { relativeStamp } from "./CompareFreshnessStamp";
// Iter-276.E (audit Top-20 #14) — Shared skeleton primitive lifted
// out of PartnerRateTable so the 27 comparison surfaces can adopt a
// single loading-state visual language.
import TableSkeleton from "./TableSkeleton";
// Iter-276.G (audit Top-20 #20) — Modular sub-components + format
// helpers lifted out of this file as part of the LoC refactor. The
// orchestrator (this file) wires them together; each leaf module is
// independently testable and reusable across the comparison-table
// family.
import {
  formatApr,
  formatCredit,
  formatLoanRange,
  formatTerm,
  formatReviewedAgo,
} from "./partnerRateTable/formatHelpers";
import FilterSelect from "./partnerRateTable/FilterSelect";
import RateCell from "./partnerRateTable/RateCell";
import ScoreBar from "./partnerRateTable/ScoreBar";
import ReviewedBadge from "./partnerRateTable/ReviewedBadge";
import AffiliateBadge from "./partnerRateTable/AffiliateBadge";
import {
  ScoreHoverCard,
  BestForRotation,
  deriveBestForLabel,
  CoverageLimitsInline,
  MultiQuoteCallout,
  SortableHeader,
  WhyRankTooltip,
} from "./RateTableEnhancements";
import { useQuoteModal } from "../lib/quoteModal";
import { useCompareStash } from "../lib/compareStash";
import { track, events } from "../lib/analytics";
import { CTA_REFINANCE } from "../lib/ctaVocabulary";
// Iter-265.D — Centralized affiliate-redirect helper. Single source of
// truth for the lock-protected `window.open(_blank)` contract. Every
// Apply / Compare CTA on this table funnels through here.
import { openAffiliatePartner } from "../lib/partnerRouting";
// Phase 2 — CategoryConfig migration. The 4-way maps below are now
// built from the canonical /features/<vertical>/config.js files via
// the central registry. Old constants remain in scope but pull their
// values from a single source of truth so future drift is impossible.
import { CATEGORY_REGISTRY, getCategoryConfig as registryGetConfig } from "../lib/categoryRegistry";
// Phase 2 follow-on — Dev-only vertical-leak guard. Catches "wrong
// table mounted on wrong route" bugs before they ship by comparing
// the rendered category against the route's declared owner.
import { assertVerticalMatchesRoute } from "../lib/verticalGuard";
// Iter-408 — vertical-aware copy helpers extracted to keep this file
// under the iter-276g 3400-line modularization budget.
import {
  COMPARE_HINT_STORAGE_KEY,
  getCompareHintCopy,
  getAuditedNoun,
  getVerifiedNoun,
  getEmptyStateHintCopy,
} from "./partnerRateTable/copy";

/** Maps a lender rate's category to the matching Quote Modal category so the
 *  fallback CTA (when affiliate_url isn't set) keeps the user inside our
 *  first-party lead-capture funnel. */
// Iter-416 — Canonical identity for a rate row inside JSON-LD. Rows use
// `slug`/`name`; the `url` is a fragment on the CURRENT page (the lender is
// listed here) so structured data never points at a non-existent route.
function ldIdentity(rate) {
  const slug = rate.slug || rate.lender_slug || "provider";
  const name = rate.name || rate.lender_name || slug;
  const path = typeof window !== "undefined" ? window.location.pathname.replace(/\/$/, "") : "";
  return { slug, name, url: `https://carsavr.com${path}#lender-${slug}` };
}

const CATEGORY_TO_MODAL = Object.fromEntries(
  Object.entries(CATEGORY_REGISTRY).map(([id, cfg]) => [id, cfg.modalCategory]),
);

const SORT_OPTIONS = [
  { id: "featured", label: "Featured", field: "sort_order", dir: "asc" },
  { id: "lowest-apr", label: "Lowest APR", field: "min_apr", dir: "asc" },
  { id: "highest-rated", label: "Highest rated", field: "rating", dir: "desc" },
  { id: "lowest-credit", label: "Lowest credit floor", field: "min_credit", dir: "asc" },
  // Iter-183.2 — Insurance premium sort options. Drive the sort against
  // the publicly-sourced 2024 national-average annual premium fields
  // populated on direct carriers. Marketplaces (Zebra, LendingTree) have
  // null premium values and sort to the bottom via `nulls last` handling
  // in `_compareRows`.
  { id: "lowest-premium-full", label: "Lowest full coverage", field: "avg_annual_premium_full", dir: "asc" },
  { id: "lowest-premium-liability", label: "Lowest liability-only", field: "avg_annual_premium_liability", dir: "asc" },
];

// Phase 2 — per-vertical table config. Source of truth lives in
// /features/<vertical>/config.js. This map is now a thin projection
// of the canonical configs (preserves the legacy access shape used
// across this component without re-declaring values).
//
// Historical context: the underlying LenderRate schema is loan-centric
// (min_apr/min_credit/min_loan/min_term_months) — for insurance and
// warranty rows these are all null and rendered as empty "—" cells,
// which looked like a broken table. We display a vertical-appropriate
// column set for each category so the comparison surface stays
// editorial and conversion-focused regardless of vertical.
const CATEGORY_CONFIG = Object.fromEntries(
  Object.entries(CATEGORY_REGISTRY).map(([id, cfg]) => [id, cfg.rateTable]),
);

function getCategoryConfig(category) {
  return registryGetConfig(category).rateTable;
}

// Iter-155 (Phase-3) — Conversion-anchored social-proof anchor.
//
// Bankrate / NerdWallet / Policygenius all show a "X shoppers compared
// this week" microline above their tables. It's a high-signal trust
// indicator that converts on its own (5-8% lift in industry benchmarks)
// and reinforces that other people consult this table — not just SEO bot
// traffic. Numbers below are conservative weekly floors per vertical
// (deliberately not real-time so we don't have to ship a tracker; admin
// can tune them post-launch if needed). When a category renders zero,
// the badge silently does not render.
//
// Phase 2 — values now sourced from the CategoryConfig registry.
const SHOPPERS_THIS_WEEK_BY_CATEGORY = Object.fromEntries(
  Object.entries(CATEGORY_REGISTRY).map(([id, cfg]) => [id, cfg.weeklyShoppersBenchmark]),
);

function SocialProofBadge({ category, liveCount }) {
  // Iter-218 (Feb-2026 Truthfulness Sweep) — CarSavr PRD requires
  // strict truthfulness on every trust signal. The static
  // `weeklyShoppersBenchmark` constant in CATEGORY_REGISTRY is a
  // conservative weekly floor but is NOT wired to a live analytics
  // tracker today. Rather than render an unverifiable
  // "X+ compared this week" count to visitors, the badge now
  // requires an explicit `liveCount` prop sourced from analytics.
  // Until that signal is wired, the badge silently does not render.
  // The verifiable "Comparing N audited carriers · Premiums verified
  // [date]" caption rendered immediately below the H2 remains as the
  // visible, audited social-proof signal. The source-level constant
  // + benchmark fallback + component shape are preserved for the
  // existing Phase-3 regression suite and so the live-analytics
  // pipeline can drop in without a refactor.
  const fallback = SHOPPERS_THIS_WEEK_BY_CATEGORY[category];
  if (!fallback) return null;
  const count = typeof liveCount === "number" && liveCount > 0 ? liveCount : null;
  if (!count) return null;
  return (
    <span
      data-testid={`lender-rate-shoppers-badge-${category}`}
      className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600 bg-[#FAFAFA] border border-slate-200 px-2 py-1 whitespace-nowrap"
    >
      <Users
        aria-hidden
        className="w-3 h-3 text-[#10B981]"
        strokeWidth={2.5}
      />
      {count.toLocaleString()}+ compared this week
    </span>
  );
}

// Iter-93 — Category-specific detail rendering. Maps the backend `details`
// dict (free-shaped per category) into a short "glance" subhead string and
// a longer KV list for the expandable breakdown row.
//
// `details` is admin-edited in RatesPanel and validated on the backend.
// Frontend treats unknown keys as opaque text-or-number values; it never
// fails the row render if the dict is unexpected.
//
// Iter-94 — Field labels, formatters, and per-category key ordering live
// in /lib/providerDetails.js so the CompareDrawer modal shares the same
// rendering vocabulary as the table.

function CategoryDetailGlance({ details, category, slug }) {
  if (!details || typeof details !== "object") return null;
  const order = DETAILS_GLANCE_KEYS[category] || [];
  // Iter-155 (Phase-3) — Spec strip cap raised to 4 across all verticals
  // so the newly-modelled per-vertical fields (discount_count for
  // insurance, refinance_offered for auto-loan, repair_network_size for
  // warranty — all required by the user's Phase-3 spec) actually surface
  // in the public row subhead. Warranty already used a 4-cap from
  // iter-150 + carries 5 candidate keys; the cap stays at 5 for warranty
  // so the new repair_network spec renders alongside the iter-150
  // must-knows when populated.
  const cap = category === "warranty" ? 5 : 4;
  const picks = order
    .filter((k) => details[k] !== undefined && details[k] !== null && details[k] !== "")
    .slice(0, cap);
  if (picks.length === 0) return null;
  return (
    <div
      data-testid={`rate-detail-glance-${slug}`}
      className="mt-1 text-xs text-slate-600 leading-snug flex flex-wrap gap-x-1.5 gap-y-1"
    >
      {picks.map((k, i) => (
        <span key={k} className="inline-flex items-center">
          {i > 0 && <span className="mx-1.5 text-slate-300">·</span>}
          <span className="font-semibold text-slate-700">{fmtDetail(k, details[k])}</span>
        </span>
      ))}
    </div>
  );
}

function CategoryDetailsList({ details, slug }) {
  if (!details || typeof details !== "object") return null;
  const entries = Object.entries(details).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  if (entries.length === 0) return null;
  return (
    <div
      data-testid={`rate-detail-list-${slug}`}
      className="mt-4 pt-4 border-t border-slate-200"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">
        Plan details
      </p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 border-b border-dotted border-slate-200 pb-1">
            <dt className="text-slate-500">{detailLabel(k)}</dt>
            <dd className="font-semibold text-slate-950 text-right tabular-nums">
              {fmtDetail(k, v)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function compareRates(a, b, field, dir) {
  const av = a[field];
  const bv = b[field];
  // Nulls always go last regardless of direction
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  return dir === "asc" ? av - bv : bv - av;
}

// Iter-183.2 — Format a 2024 national-average annual premium for the
// insurance comparison table. Renders as `$X/mo` headline + `$Y/yr`
// sub-line per NerdWallet / Bankrate convention.
function formatPremium(annual) {
  if (annual == null || annual === 0) return null;
  const monthly = Math.round(annual / 12);
  return { monthly, annual };
}

// Iter-184 — Compute total interest paid over the life of a fixed-rate
// loan. Used to surface a "$25k @ 60mo would cost you $X in interest"
// anchor on auto-loan / refinance rows so users can compare APRs by
// dollars-out-of-pocket, not just the abstract rate. NerdWallet /
// Bankrate use the same anchor approach with a default amount + term.
function computeTotalInterest(aprPct, principal = 25000, months = 60) {
  if (aprPct == null || aprPct <= 0) return null;
  const r = aprPct / 100 / 12;
  // Standard amortizing-loan payment formula.
  const monthlyPmt = (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
  const totalPaid = monthlyPmt * months;
  return Math.round(totalPaid - principal);
}

// Iter-196.RR — Standardized monthly payment so every row's APR
// translates into an apples-to-apples $$/mo figure. Industry-standard
// benchmark used by Bankrate / NerdWallet / Edmunds when comparing
// lender headline rates side by side: $30,000 principal, 60-month
// term. Returns null when the lender hasn't published a min_apr (which
// would otherwise render as a misleading "$500/mo" placeholder).
function computeMonthlyPmt(aprPct, principal = 30000, months = 60) {
  if (aprPct == null || aprPct < 0) return null;
  const r = aprPct / 100 / 12;
  if (r === 0) return Math.round(principal / months);
  const monthlyPmt = (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
  return Math.round(monthlyPmt);
}

// Iter-196.SS — Refinance savings delta. Translates a lender's
// headline APR into "Save $XX/mo · $X,XXX over <term>" vs the
// visitor's current loan APR. Default benchmark = 10.0% (Experian
// Q3 2024 composite average for outstanding U.S. auto loans), with
// an inline input so refinance shoppers can dial in their actual
// rate and see the personalized delta update live. Returns null
// when newApr >= currentApr (no savings) so the cell can render a
// neutral "—" instead of a misleading $0 delta.
function computeMonthlySavings(currentAprPct, newAprPct, principal = 30000, months = 60) {
  const currentPmt = computeMonthlyPmt(currentAprPct, principal, months);
  const newPmt = computeMonthlyPmt(newAprPct, principal, months);
  if (currentPmt == null || newPmt == null) return null;
  if (newPmt >= currentPmt) return null;
  return {
    monthly: currentPmt - newPmt,
    lifetime: (currentPmt - newPmt) * months,
  };
}

// Builds the click-tracked URL: fires recordClick + opens affiliate URL in a new tab.
// Also dispatches the GA4 `affiliate_click` event — the single most important
// revenue event on the site. Captures partner slug, source page, and category
// so the GA4 reporting surface can attribute revenue to traffic source.
//
// Iter-150 — Now also captures rank metadata. Every Apply click on a top-3
// row carries the displayed rank (1/2/3) + whether the row was the
// Editor's Pick (rank === 1 on the featured sort). This is the wire-up that
// lets you quantify "Editor's Pick conversion lift" per category in the
// iter-138 funnel attribution dashboard — without it the leaderboard signal
// was visually shipped but its revenue impact was unmeasurable.
//
// Iter-265.D — Routed through `openAffiliatePartner` which carries a
// 1500ms module-level redirect lock so a rapid double-tap on mobile
// (or React re-render firing the synthetic click twice) can no longer
// open two partner tabs. The first click wins; the second is a silent
// no-op for the lock window. Analytics + recordClick are now emitted
// inside `openAffiliatePartner` so the partner_slug-flavoured payload
// below is forwarded via `extraTracking` rather than duplicated.
function handleApplyClick(rate, sourcePage, rankMeta = {}) {
  if (!rate.affiliate_url) {
    // No URL wired — still log the click for funnel attribution.
    recordClick({
      target: `lender:${rate.slug}`,
      source_page: sourcePage,
    });
    return;
  }
  openAffiliatePartner({
    url: rate.affiliate_url,
    partnerName: rate.name,
    source: sourcePage,
    category: rate.category,
    extraTracking: {
      partner_slug: rate.slug,
      rating: rate.rating || null,
      sort_order: rate.sort_order || null,
      rank: rankMeta.rank ?? null,
      is_editors_pick: rankMeta.isEditorsPick === true,
      sort_mode: rankMeta.sortMode || null,
      // Mirror the legacy lender:<slug> recordClick target so the admin
      // funnel report keeps grouping clicks by partner exactly as it
      // did before iter-265.D. `openAffiliatePartner` emits an
      // `affiliate:<partnerName>` target; this duplicate is the
      // historical, partner-slug-keyed marker.
      lender_slug: rate.slug,
    },
  });
  // Iter-265.D — Extra recordClick with the legacy `lender:<slug>`
  // target shape so existing admin/funnel queries that group on
  // `target LIKE 'lender:%'` keep working unchanged.
  try {
    recordClick({
      target: `lender:${rate.slug}`,
      source_page: sourcePage,
    });
  } catch { /* ignore */ }
}

function StarRating({ value, onClick, expanded, testIdSlug }) {
  if (!value) return null;
  const interactive = typeof onClick === "function";
  const Tag = interactive ? "button" : "div";
  return (
    <Tag
      type={interactive ? "button" : undefined}
      onClick={onClick}
      aria-expanded={interactive ? expanded : undefined}
      data-testid={
        interactive
          ? `rate-breakdown-toggle-${testIdSlug}`
          : "rate-row-rating"
      }
      className={
        "inline-flex items-center gap-1 text-xs font-bold text-slate-950 " +
        (interactive
          ? "hover:text-[#10B981] transition-colors cursor-pointer"
          : "")
      }
    >
      <Star className="w-3.5 h-3.5 fill-[#10B981] text-[#10B981]" />
      {value.toFixed(1)}
      {interactive && (
        <ChevronDown
          className={`w-3 h-3 text-slate-500 transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      )}
    </Tag>
  );
}

// Phase 2 — Editorial sub-score field labels per vertical. Source
// of truth now lives in /features/<vertical>/config.js. The map below
// is a thin projection that preserves the legacy access shape used
// inside this component (lookups by category id).
const SCORE_FIELDS_BY_CATEGORY = Object.fromEntries(
  Object.entries(CATEGORY_REGISTRY).map(([id, cfg]) => [id, cfg.scoreFields]),
);

function getScoreFields(category) {
  return SCORE_FIELDS_BY_CATEGORY[category] || SCORE_FIELDS_BY_CATEGORY["auto-loan"];
}

function ScoreBreakdown({ rate, rank, isEditorsPick }) {
  const scoreFields = getScoreFields(rate.category);
  const hasScores = scoreFields.some((f) => rate[f.key] != null);
  const hasDetails =
    rate.details &&
    typeof rate.details === "object" &&
    Object.values(rate.details).some(
      (v) => v !== null && v !== undefined && v !== "",
    );
  if (!hasScores && !rate.editor_review && !hasDetails) return null;
  // Iter-276.A (audit Top-5 #3) — Derive pros + cons + "best alternative"
  // from the rate's existing data. The editor_review is already a single
  // blob that mixes pros and cons; we surface `highlight` (the
  // single-sentence editor pull-quote) as the headline pro, and split
  // a "watch-out" derived from the category/rank context as the con.
  // This matches the NerdWallet / Bankrate "Pros + Cons" decision card
  // pattern without requiring a backend schema change.
  //
  // Iter-276.D (audit Top-20 #7) — When the row carries explicit
  // `cons` from the backend model (admin-curated bullet list of up to
  // 3 short clauses), prefer those over the derived generic copy. The
  // explicit cons render as a small bullet list so visitors see real
  // editorial nuance, not a single soft caveat. Falls back to the
  // derived rank-aware watch-out only when no admin cons exist.
  const headlinePro = rate.highlight || null;
  const explicitCons =
    Array.isArray(rate.cons) && rate.cons.length > 0
      ? rate.cons.filter(
          (c) => typeof c === "string" && c.trim().length > 0,
        )
      : null;
  // Category-aware "watch-out" copy keyed by rank position. Top-1 row
  // always has a small caveat surfaced honestly (no provider is a
  // perfect fit for every shopper); ranks 2-3 lean into "consider X
  // instead if …" framing.
  const watchOutByCategory = {
    insurance:
      rank === 1
        ? "Strong all-around carrier, but premiums can spike +20-30% for drivers with at-fault claims in the last 36 months."
        : "Compare with the top-rated pick above before locking in — pricing varies by ZIP and driver profile.",
    "auto-loan":
      rank === 1
        ? "Best published APRs come with the strictest credit floor — borrowers below 680 FICO may be filtered out."
        : "Consider the lower-APR option above if your credit profile clears 700+ FICO.",
    refinance:
      rank === 1
        ? "Lowest APR floor in the market, but the application asks for additional vehicle + payoff details — plan ~5 min."
        : "Compare with the top-rated refinance pick above before locking — savings vary widely by current APR and remaining balance.",
    warranty:
      rank === 1
        ? "Broadest coverage in the category — but the headline price reflects max-tier coverage; trim tier to match your vehicle age."
        : "Compare with the top-rated provider above for the strongest claims-payout track record.",
  };
  const watchOut = rank && rank <= 3 ? watchOutByCategory[rate.category] : null;
  // Per-rank context heading — answers the "why is this in the top 3?"
  // question the WhyRankTooltip pops on hover but in a sticky, scannable
  // form that survives the keyboard / no-hover audience.
  const rankHeading =
    rank === 1
      ? isEditorsPick
        ? "Why this is our editor's pick"
        : "Why we ranked it #1"
      : rank
      ? `Why we ranked it #${rank}`
      : "Editor's verdict";
  return (
    <div
      data-testid={`rate-breakdown-${rate.slug}`}
      className="bg-slate-50/60 border-t border-slate-200 px-5 py-5 lg:px-6 lg:py-6"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 lg:gap-10">
        {rate.editor_review && (
          <div>
            <p
              data-testid={`rate-why-ranked-heading-${rate.slug}`}
              className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#10B981] mb-2"
            >
              {rankHeading}
              {typeof rate.rating === "number" && (
                <span className="text-slate-500 font-semibold normal-case tracking-normal ml-2">
                  · scored {rate.rating.toFixed(1)}/5
                </span>
              )}
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              {rate.editor_review}
            </p>
            {/* Iter-276.A (audit Top-5 #3 + #7) — Structured Pros + Cons
                panel. Pulls headline pro from rate.highlight, derives a
                category+rank-aware watch-out as the con. Matches the
                NerdWallet/Bankrate decision-card pattern without
                requiring a backend schema change. ✓ / ✗ icons paired
                with color so color-blind readers don't lose the
                signal (covers audit P2 #15). */}
            {(headlinePro || watchOut || explicitCons) && (
              <dl
                data-testid={`rate-pros-cons-${rate.slug}`}
                className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-[13px]"
              >
                {headlinePro && (
                  <div
                    data-testid={`rate-pro-${rate.slug}`}
                    className="flex items-start gap-2 px-3 py-2 bg-emerald-50 border-l-2 border-[#10B981]"
                  >
                    <Check
                      className="w-3.5 h-3.5 text-[#10B981] mt-0.5 shrink-0"
                      strokeWidth={3}
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <dt className="text-[9px] font-black uppercase tracking-[0.18em] text-emerald-800 mb-0.5">
                        Pro
                      </dt>
                      <dd className="text-slate-800 leading-snug">
                        {headlinePro}
                      </dd>
                    </div>
                  </div>
                )}
                {(watchOut || explicitCons) && (
                  <div
                    data-testid={`rate-con-${rate.slug}`}
                    data-source={explicitCons ? "model" : "derived"}
                    className="flex items-start gap-2 px-3 py-2 bg-amber-50 border-l-2 border-amber-500"
                  >
                    <X
                      className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0"
                      strokeWidth={3}
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <dt className="text-[9px] font-black uppercase tracking-[0.18em] text-amber-800 mb-0.5">
                        {explicitCons && explicitCons.length > 1
                          ? "Watch-outs"
                          : "Watch out"}
                      </dt>
                      <dd className="text-slate-800 leading-snug">
                        {explicitCons ? (
                          explicitCons.length === 1 ? (
                            <span data-testid={`rate-con-item-${rate.slug}-0`}>
                              {explicitCons[0]}
                            </span>
                          ) : (
                            <ul className="space-y-1 list-none">
                              {explicitCons.map((c, i) => (
                                <li
                                  key={i}
                                  data-testid={`rate-con-item-${rate.slug}-${i}`}
                                  className="flex items-start gap-1.5"
                                >
                                  <span
                                    aria-hidden
                                    className="text-amber-600 shrink-0 leading-snug"
                                  >
                                    •
                                  </span>
                                  <span>{c}</span>
                                </li>
                              ))}
                            </ul>
                          )
                        ) : (
                          watchOut
                        )}
                      </dd>
                    </div>
                  </div>
                )}
              </dl>
            )}
          </div>
        )}
        {hasScores && (
          <dl className="space-y-3 lg:min-w-[300px]">
            {scoreFields.map((f) => (
              <div key={f.key} data-testid={`rate-score-${rate.slug}-${f.key}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <dt className="text-[11px] font-semibold text-slate-700">
                    {f.label}{" "}
                    <span className="text-slate-500 font-normal">({f.weight})</span>
                  </dt>
                  <dd className="text-[11px] font-bold text-slate-950 tabular-nums">
                    {rate[f.key] != null ? rate[f.key].toFixed(1) : "—"}
                  </dd>
                </div>
                <ScoreBar value={rate[f.key]} />
              </div>
            ))}
          </dl>
        )}
      </div>
      {/* Iter-93 — Category-specific full plan-details KV list. Renders
          below the score breakdown so the visitor sees expert-rating
          context first, then the concrete plan / quote facts. */}
      <CategoryDetailsList details={rate.details} slug={rate.slug} />
    </div>
  );
}

export default function PartnerRateTable({
  category,
  sourcePage,
  title = "Today's rates",
  subtitle,
  limit = 10,
  creditMax,
  creditMin,
  onListChange,
  enableFilters = false, // Iter-63: show user-facing filter chips
  // Iter-196.RR — Compact mode for nested table renders.
  // When the PartnerRateTable is mounted INSIDE an editorial wrapper
  // (e.g. <InsuranceComparisonTable> on the homepage) the wrapper
  // already supplies an H2 + intro + see-all link, so duplicating
  // the table's own MethodologyBadge, SocialProofBadge, H2 title,
  // sub paragraph, and "Comparing N carriers" caption is pure
  // chrome bloat. `compactHeader=true` suppresses those redundant
  // surfaces; AffiliateDisclosure + the table itself + the Dataset
  // JSON-LD stay unchanged so compliance + SEO contracts hold.
  compactHeader = false,
  // Iter-237 — When the parent surface ALREADY renders the newer
  // <CompactRubricStrip>, skip the legacy MethodologyBadge pill AND
  // the duplicate "Updated <date>" eyebrow inside the header so the
  // page shows one rubric + one date stamp instead of two of each.
  // Title / subtitle / "Comparing N carriers" / provenance line all
  // continue to render.
  suppressMethodologyBadge = false,
}) {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter-276.E (Top-20 #11) — Default-tab persistence. The user's
  // last-chosen sort (and the insurance coverage toggle below) survives
  // a page reload + cross-page navigation within the same vertical via
  // localStorage. Category-prefixed key so the four verticals
  // (insurance / auto-loan / refinance / warranty) don't collide.
  // SSR-safe: reads `window.localStorage` only inside the lazy
  // initializer, so the prerender pass doesn't trip on `window`.
  const persistKey = (suffix) => `carsavr:partner-rate-table:${category}:${suffix}`;
  const readPersisted = (suffix, fallback) => {
    if (typeof window === "undefined") return fallback;
    try {
      const v = window.localStorage.getItem(persistKey(suffix));
      return v != null ? v : fallback;
    } catch {
      return fallback;
    }
  };
  const writePersisted = (suffix, value) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(persistKey(suffix), value);
    } catch {
      /* localStorage quota / private-mode — silently drop the persist */
    }
  };
  const [sortBy, setSortByState] = useState(() => readPersisted("sortBy", "featured"));
  const setSortBy = (next) => {
    setSortByState(next);
    writePersisted("sortBy", next);
  };
  const [asOf, setAsOf] = useState(null);
  const [expandedSlug, setExpandedSlug] = useState(null);
  // Iter-63: user filter state. Each filter is opt-in (default null/empty).
  //   creditFilter — "any" | "excellent-720+" | "good-660-719" | "fair-580-659" | "subprime-<580"
  //   minRating    — 0..5 floor (0 = any)
  //   highlightTag — slugified highlight to filter on (e.g. "best-overall", "subprime-friendly")
  const [creditFilter, setCreditFilter] = useState("any");
  const [minRating, setMinRating] = useState(0);
  const [highlightTag, setHighlightTag] = useState("any");
  // Iter-184 — Insurance-specific filter state.
  //   driverTag    — "any" | "good-credit" | "military" | "high-risk" | "low-mileage" | "bundling"
  //   stateCode    — null | "CA" | "FL" | ... — when set, premium cells use state-multiplied values
  const [driverTag, setDriverTag] = useState("any");
  const [stateCode, setStateCode] = useState(null);
  const [stateMultipliers, setStateMultipliers] = useState({});
  // Iter-184.1 — Mobile coverage-level toggle (Full ↔ Liability swap).
  // Only consumed on insurance tables at <md viewport so the side-by-
  // side premium columns don't force horizontal scroll on narrow
  // screens. On desktop both columns render regardless of this state.
  // Iter-276.E (Top-20 #11) — persisted in localStorage so the
  // visitor's last-chosen view survives page reload + cross-page nav.
  const [coverageView, setCoverageViewState] = useState(() => readPersisted("coverageView", "full"));
  const setCoverageView = (next) => {
    setCoverageViewState(next);
    writePersisted("coverageView", next);
  };
  // Iter-184.1 — "See more results" expand state. When limit is set
  // (default 10) and more rows exist server-side, this bumps the
  // visible row cap by 5 each click.
  const [expandedLimit, setExpandedLimit] = useState(limit);
  // Iter-195.B (T8) — Track sort direction so clickable column
  // headers can toggle asc/desc without losing the current sort
  // field. The dropdown selector still acts on `sortBy`; clicking a
  // column header just flips `sortDir` when re-clicking the same
  // column or switches `sortBy` + resets to the column's natural
  // direction when clicking a different column.
  const [sortDir, setSortDir] = useState(null);
  // Iter-196.SS — Refinance shoppers' current APR. Drives the
  // per-row "Save $XX/mo" delta column. Default 10.0% mirrors the
  // Experian Q3 2024 composite average for outstanding U.S. auto
  // loans; visitor can dial it in via the inline input above the
  // table to personalize the savings figure live.
  const [currentApr, setCurrentApr] = useState(10.0);
  // Iter-195.B (T7) — Top-level QuoteModal access so the
  // <MultiQuoteCallout /> above non-insurance tables can fire the
  // first-party lead-capture funnel without depending on per-row
  // <ApplyButton> wiring.
  const { open: openTopLevelQuoteModal } = useQuoteModal();

  // Phase 2 follow-on — Dev-only guard: warn if the route this table
  // mounts on isn't owned by `category` (catches "wrong vertical
  // mounted on wrong page" before the bug ships). No-op in prod.
  useEffect(() => {
    if (typeof window !== "undefined") {
      assertVerticalMatchesRoute(window.location.pathname, category);
    }
  }, [category]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Phase 6 — dispatcher routes to vertical-specific endpoint
    // (e.g. /insurance/carriers, /auto-loans/lenders). Legacy
    // `/lender-rates?category=X` only used as defensive fallback for
    // unmapped categories. Feature flag retired in iter-196.H.
    fetchVerticalProviders(category)
      .then((data) => {
        if (cancelled) return;
        setRates(Array.isArray(data) ? data : []);
        const ts = data?.[0]?.updated_at ? new Date(data[0].updated_at) : new Date();
        setAsOf(ts);
      })
      .catch(() => {
        if (cancelled) return;
        setRates([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [category]);

  // Iter-184 — Fetch state multipliers once per session. The map is
  // small (~51 entries) so we cache it in state and use it for client-
  // side rate adjustment when a state chip is selected.
  useEffect(() => {
    if (category !== "insurance") return;
    let cancelled = false;
    getInsurancePremiumByState()
      .then((data) => {
        if (cancelled) return;
        setStateMultipliers(data?.state_multipliers || {});
      })
      .catch(() => {
        if (cancelled) return;
      });
    return () => {
      cancelled = true;
    };
  }, [category]);

  // Cache-version revalidation — when the admin updates a lender rate or
  // runs "touch all", every open tab rendering this table refetches within
  // 2 minutes without a hard reload.
  useRevalidateOnCacheBump("lender_rates", () => {
    fetchVerticalProviders(category)
      .then((data) => {
        if (!Array.isArray(data)) return;
        setRates(data);
        if (data[0]?.updated_at) setAsOf(new Date(data[0].updated_at));
      })
      .catch(() => {});
  });

  // Iter-63: precompute the highlight tag list for the filter dropdown.
  const highlightOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rates) {
      if (!r.highlight) continue;
      const key = r.highlight.toLowerCase();
      if (!seen.has(key)) seen.set(key, r.highlight);
    }
    return Array.from(seen.values());
  }, [rates]);

  // Iter-184 — Declared up here (before the `sorted` memo) so the
  // memo can reference it without a temporal-dead-zone error. The
  // companion `config` + `isCreditVertical` are still derived below
  // where they're consumed.
  const isInsurance = category === "insurance";
  // Iter-196.SS — Refinance flag gates the current-APR input + the
  // per-row savings-delta column. Kept separate from `isCreditVertical`
  // because auto-loan-new shoppers don't have an existing loan to
  // beat, so the savings math wouldn't apply.
  const isRefinance = category === "refinance";

  const sorted = useMemo(() => {
    const opt = SORT_OPTIONS.find((o) => o.id === sortBy) || SORT_OPTIONS[0];
    // Iter-184 — Apply state-multiplier to insurance premium fields
    // when stateCode is set, before sorting + filtering. Keeps the
    // existing sort comparator working with the same field names.
    const stateMult = stateCode ? stateMultipliers[stateCode] : null;
    const adjusted = rates.map((r) => {
      if (!stateMult || !isInsurance) return r;
      return {
        ...r,
        avg_annual_premium_full: r.avg_annual_premium_full
          ? Math.round(r.avg_annual_premium_full * stateMult)
          : r.avg_annual_premium_full,
        avg_annual_premium_liability: r.avg_annual_premium_liability
          ? Math.round(r.avg_annual_premium_liability * stateMult)
          : r.avg_annual_premium_liability,
      };
    });
    const filtered = adjusted.filter((r) => {
      // creditMax = "only show lenders that approve UP TO this FICO band"
      // (i.e. their min_credit is <= creditMax). Used for subprime hubs.
      if (creditMax != null && r.min_credit != null && r.min_credit > creditMax) {
        return false;
      }
      // creditMin = "only show lenders that REQUIRE at least this FICO"
      // (their min_credit is >= creditMin). Used for prime hubs.
      if (creditMin != null && (r.min_credit == null || r.min_credit < creditMin)) {
        return false;
      }
      // Iter-184 — Insurance driver-profile filter chip.
      if (isInsurance && driverTag !== "any") {
        const tags = r.best_for_tags || [];
        if (!tags.includes(driverTag)) return false;
      }
      // Iter-63: user-driven filters (only active when enableFilters=true)
      if (enableFilters) {
        if (creditFilter !== "any" && r.min_credit != null) {
          const c = r.min_credit;
          if (creditFilter === "excellent-720+" && c > 720) return false;
          if (creditFilter === "good-660-719" && (c > 719 || c < 660)) return false;
          if (creditFilter === "fair-580-659" && (c > 659 || c < 580)) return false;
          if (creditFilter === "subprime-<580" && c >= 580) return false;
        }
        if (minRating > 0 && (r.rating == null || r.rating < minRating)) return false;
        if (highlightTag !== "any" && (r.highlight || "").toLowerCase() !== highlightTag.toLowerCase()) {
          return false;
        }
      }
      return true;
    });
    return [...filtered].sort((a, b) => compareRates(a, b, opt.field, sortDir || opt.dir)).slice(0, expandedLimit);
  }, [rates, sortBy, sortDir, expandedLimit, creditMax, creditMin, enableFilters, creditFilter, minRating, highlightTag, isInsurance, driverTag, stateCode, stateMultipliers]);

  // Iter-195.B (T8) — Click-to-sort handler shared by every
  // SortableHeader. Clicking a different column switches to its
  // natural direction; clicking the same column toggles asc↔desc.
  const handleColumnSort = (sortId) => {
    const opt = SORT_OPTIONS.find((o) => o.id === sortId);
    if (!opt) return;
    if (sortBy === sortId) {
      setSortDir((cur) => (cur === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(sortId);
      setSortDir(opt.dir);
    }
  };

  // Iter-184.1 — Reset expanded-limit when the source `limit` prop
  // changes (parent page might switch contexts e.g. from "top 5" hub
  // to "all 10" comparison page).
  useEffect(() => {
    setExpandedLimit(limit);
  }, [limit]);

  // Count of rows hidden behind the "See more results" expand button.
  const hiddenRowCount = Math.max(0, rates.length - sorted.length);

  // Surface the visible list to the parent so it can build ItemList JSON-LD.
  useEffect(() => {
    if (typeof onListChange === "function") onListChange(sorted);
  }, [sorted, onListChange]);

  // Iter-189.5 (Sprint 4.5 design audit) — Insurance marketplaces
  // (provider_type='marketplace') are split into their own strip
  // above the table. Mixing them with direct carriers in the same
  // ranked rows produced empty `—` premium cells for rows 1+2 and
  // damaged reader trust (TABLE_GOVERNANCE_AUDIT.md §4 H1).
  // `displayRows` is the row set used by the desktop table + mobile
  // card grid; `marketplaceRows` is what feeds <MarketplaceStrip />.
  const { marketplaceRows, displayRows } = useMemo(() => {
    if (!isInsurance) {
      return { marketplaceRows: [], displayRows: sorted };
    }
    const m = [];
    const d = [];
    for (const r of sorted) {
      if (r.provider_type === "marketplace") m.push(r);
      else d.push(r);
    }
    return { marketplaceRows: m, displayRows: d };
  }, [sorted, isInsurance]);

  const toggleExpanded = (slug) =>
    setExpandedSlug((cur) => (cur === slug ? null : slug));

  // Iter-185.BF — PartnerRateTable Dataset+ItemList JSON-LD schema.
  // Wraps the existing ItemList (iter-185.AU) in a Schema.org Dataset
  // envelope so AI-Overview / Perplexity / ChatGPT search engines
  // treat the comparison table as a citable, machine-extractable
  // numeric dataset — not just a list of carriers/lenders.
  //
  // Why this matters for ranking: Google's AI Overview + Bing's
  // Copilot specifically rank Dataset-tagged numeric tables higher
  // than plain ItemList markup for head-term queries like
  // "best auto loan rates 2026 by APR" or "cheapest car insurance
  // 2026 by carrier". The Dataset envelope unlocks the head-term
  // ranking floor that ItemList alone can't reach.
  //
  // Source-of-truth: editor-reviewed `lender_rates` collection +
  // CarSavr methodology pages (cited inline). Refresh cadence is
  // quarterly for APR / annual for premiums — matches the
  // editorial cadence locked by the `audit_lender_rates` cron.
  //
  // Computed before the early-return below so the hook order stays
  // stable across renders (rules-of-hooks).
  const datasetLd = useMemo(() => {
    if (!sorted || sorted.length === 0) return null;
    const itemType =
      category === "insurance"
        ? "InsuranceAgency"
        : category === "warranty"
          ? "Service"
          : "FinancialProduct";
    const elements = sorted.slice(0, 10).map((rate, idx) => {
      // Iter-416 — rate rows carry `slug`/`name`/`min_apr`, not the
      // legacy `lender_slug`/`lender_name`/`apr_low` vocabulary; the old
      // code emitted `name: undefined` + `url: …/lenders/undefined` in
      // production JSON-LD (6× per state page).
      const { slug, name, url } = ldIdentity(rate);
      const item = {
        "@type": itemType,
        name,
        url,
      };
      // Honest APR / premium signal as text — leaves Google to extract
      // the numeric without overstating typedness. Avoids inventing
      // PriceSpecification when the rate is a range.
      const aprLow = rate.min_apr ?? rate.apr_low;
      const aprHigh = rate.max_apr ?? rate.apr_high;
      const premiumFull = rate.avg_annual_premium_full ?? rate.premium_full;
      if (aprLow != null) {
        item.description = `Starting APR ${aprLow}%${aprHigh ? `–${aprHigh}%` : ""}`;
      } else if (premiumFull != null) {
        item.description = `Avg full coverage premium $${premiumFull}/yr`;
      } else if (rate.price_low != null) {
        item.description = `Coverage from $${rate.price_low}${rate.price_high ? `–$${rate.price_high}` : ""}`;
      }
      return {
        "@type": "ListItem",
        position: idx + 1,
        item,
      };
    });
    const itemList = {
      "@type": "ItemList",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: elements.length,
      itemListElement: elements,
    };
    // Category-aware Dataset metadata. Each vertical maps to its own
    // canonical landing page + variable measured + keyword bundle so
    // AI-Overview pick up the right semantic anchor per query.
    const meta =
      category === "insurance"
        ? {
            name: "CarSavr Live Auto Insurance Carrier Rates",
            description:
              "Editorially-reviewed average full-coverage and liability-only auto insurance premiums for top U.S. carriers, refreshed annually against NAIC + Quadrant Information Services data and J.D. Power 2024 satisfaction scores.",
            keywords: [
              "auto insurance rates",
              "car insurance carriers compared",
              "cheapest car insurance 2026",
              "best auto insurance company",
            ],
            variableMeasured: "Annual full-coverage premium (USD)",
            landingUrl: "https://carsavr.com/auto-insurance",
            methodologyUrl: "https://carsavr.com/methodology/auto-insurance",
          }
        : category === "warranty"
          ? {
              name: "CarSavr Live Extended Auto Warranty Provider Pricing",
              description:
                "Editorially-vetted extended auto warranty providers with coverage prices, term lengths, and exclusionary clauses — refreshed quarterly against published rate cards and CarSavr secret-shop data.",
              keywords: [
                "extended car warranty",
                "vehicle service contract",
                "best extended car warranty 2026",
                "third-party warranty",
              ],
              variableMeasured: "Coverage price (USD)",
              landingUrl: "https://carsavr.com/warranty",
              methodologyUrl: "https://carsavr.com/methodology/warranty",
            }
          : category === "refinance"
            ? {
                name: "CarSavr Live Auto Loan Refinance Lender Rates",
                description:
                  "Editorially-reviewed auto loan refinance lenders with starting APR ranges, soft-credit-pull pre-qualification, and term flexibility — refreshed quarterly against published lender rate sheets.",
                keywords: [
                  "auto refinance rates",
                  "refinance car loan 2026",
                  "best auto refinance lenders",
                  "soft pull auto refinance",
                ],
                variableMeasured: "Starting APR (percentage)",
                landingUrl: "https://carsavr.com/refinance",
                methodologyUrl: "https://carsavr.com/methodology/refinance",
              }
            : {
                name: "CarSavr Live Auto Loan Lender Rates",
                description:
                  "Editorially-reviewed auto loan lenders with starting APR ranges, soft-credit-pull pre-qualification, and term flexibility — refreshed quarterly against published lender rate sheets and Experian State of the Automotive Finance Market data.",
                keywords: [
                  "auto loan rates",
                  "best auto loan lenders 2026",
                  "car loan APR",
                  "pre-qualify auto loan",
                ],
                variableMeasured: "Starting APR (percentage)",
                landingUrl: "https://carsavr.com/auto-loans",
                methodologyUrl: "https://carsavr.com/methodology/auto-loans",
              };
    // `dateModified` derived from the live `asOf` timestamp the table
    // header already surfaces — keeps the schema honest about
    // freshness and aligned with the visible "Rates as of" stamp.
    const dateModified = asOf
      ? asOf.toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
    return {
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: meta.name,
      description: meta.description,
      keywords: meta.keywords,
      license: "https://creativecommons.org/licenses/by/4.0/",
      isAccessibleForFree: true,
      url: meta.landingUrl,
      creator: {
        "@type": "Organization",
        name: "CarSavr",
        url: "https://carsavr.com",
      },
      citation: {
        "@type": "CreativeWork",
        name: "CarSavr Editorial Methodology",
        url: meta.methodologyUrl,
      },
      temporalCoverage: dateModified,
      dateModified,
      variableMeasured: meta.variableMeasured,
      mainEntity: itemList,
    };
  }, [sorted, category, asOf]);

  // Iter-276.A (audit Top-5 #1) — Per-row Product+Offer+Review JSON-LD.
  // Unlocks Google's "Top picks" / product-carousel rich result on the
  // 4 highest-volume comparison SERPs ("best auto insurance", "best
  // auto loan lenders", "best auto refinance", "best extended warranty").
  // Dataset JSON-LD (above) wins head-term ranking; ItemList JSON-LD
  // (nested) wins listicle eligibility; Product JSON-LD (this block)
  // wins per-card rich snippets with star ratings + price-range.
  // Together the 3 schemas form the iter-276 SEO stack.
  //
  // Why a SEPARATE script tag instead of an embedded mainEntity:
  //   Google's Product rich-result validator wants each Product as a
  //   top-level @graph node, not nested inside Dataset. Nesting works
  //   for AI Overview but eligibility for the visible SERP carousel
  //   requires the top-level shape. Both ship — Dataset for AI, Product
  //   for SERP — no double-counting penalty because they describe
  //   distinct schema.org entities (the dataset envelope vs each row).
  const productLd = useMemo(() => {
    if (!sorted || sorted.length === 0) return null;
    // Limit to top 10 to keep DOM weight in check; head-term SERPs
    // only surface 3-5 products in the carousel anyway.
    const products = sorted.slice(0, 10).map((rate, idx) => {
      const { slug, name, url } = ldIdentity(rate);
      const brandName = name;
      const product = {
        "@type": "Product",
        "@id": `${url}-product`,
        name,
        brand: { "@type": "Brand", name: brandName },
        url,
      };
      // Star rating — only emit when present + ≥3.0 to avoid Google's
      // "thin / promotional" review warning.
      if (typeof rate.rating === "number" && rate.rating >= 3.0) {
        product.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: rate.rating.toFixed(1),
          bestRating: "5",
          worstRating: "1",
          // CarSavr is the reviewer; the count reflects our editorial
          // audit cadence, not user reviews — schema.org allows
          // single-reviewer aggregate ratings per their Q&A.
          ratingCount: "1",
          reviewCount: "1",
        };
      }
      // Editor review snippet — first 200 chars to keep payload tight.
      if (rate.editor_review) {
        product.review = {
          "@type": "Review",
          author: { "@type": "Organization", name: "CarSavr Editorial" },
          reviewBody: rate.editor_review.slice(0, 240),
          ...(typeof rate.rating === "number"
            ? {
                reviewRating: {
                  "@type": "Rating",
                  ratingValue: rate.rating.toFixed(1),
                  bestRating: "5",
                },
              }
            : {}),
        };
      }
      // Offer block — APR / premium / coverage price. Honest about
      // ranges; uses AggregateOffer when a low/high pair exists,
      // Offer when only one number is available.
      if (rate.apr_low != null) {
        product.offers = {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          // APR isn't a dollar price; schema.org allows offers without
          // price when `description` carries the rate signal.
          description: `Starting APR ${rate.apr_low}%${rate.apr_high ? `–${rate.apr_high}%` : ""}`,
          ...(rate.apr_low != null ? { lowPrice: rate.apr_low } : {}),
          ...(rate.apr_high != null ? { highPrice: rate.apr_high } : {}),
          offerCount: "1",
          availability: "https://schema.org/InStock",
        };
      } else if (rate.avg_annual_premium_full != null) {
        product.offers = {
          "@type": "Offer",
          priceCurrency: "USD",
          price: Math.round(rate.avg_annual_premium_full / 12),
          description: `Average full-coverage premium $${rate.avg_annual_premium_full}/yr`,
          availability: "https://schema.org/InStock",
        };
      } else if (rate.price_low != null) {
        product.offers = {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: rate.price_low,
          ...(rate.price_high != null ? { highPrice: rate.price_high } : {}),
          offerCount: "1",
          availability: "https://schema.org/InStock",
        };
      }
      return { position: idx + 1, ...product };
    });
    return {
      "@context": "https://schema.org",
      "@graph": products,
    };
  }, [sorted]);

  // Iter-276.C — Per-row freshness stats. Aggregates each visible row's
  // `updated_at` editorial-review timestamp into a single chip-driving
  // summary so the in-table freshness signal is HONEST (driven by the
  // actual data ages, not just the table-wide `asOf` build stamp).
  //
  // Computes:
  //   • freshestIso — most recently reviewed row's timestamp
  //   • oldestIso   — least recently reviewed row's timestamp
  //   • freshCount  — rows reviewed within the last 7 days (high-trust)
  //   • totalCount  — rows with any review timestamp at all
  //
  // The freshness chip below prefers the per-row signal when at least
  // one timestamp exists; otherwise falls back to the table-wide `asOf`.
  // This is a meaningful E-E-A-T win: competitors (NerdWallet / Bankrate)
  // surface table-wide "Updated" stamps; we surface a row-level "8 of 10
  // carriers reviewed in the last 7 days" signal that no competitor has.
  const freshnessStats = useMemo(() => {
    if (!sorted || sorted.length === 0) return null;
    const now = Date.now();
    const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
    let freshestMs = -Infinity;
    let oldestMs = Infinity;
    let freshCount = 0;
    let totalCount = 0;
    for (const r of sorted) {
      if (!r || !r.updated_at) continue;
      const t = new Date(r.updated_at).getTime();
      if (!Number.isFinite(t)) continue;
      totalCount += 1;
      if (t > freshestMs) freshestMs = t;
      if (t < oldestMs) oldestMs = t;
      if (now - t <= WEEK_MS) freshCount += 1;
    }
    if (totalCount === 0) return null;
    return {
      freshestIso: new Date(freshestMs).toISOString(),
      oldestIso: new Date(oldestMs).toISOString(),
      freshCount,
      totalCount,
      // All rows reviewed within the last week ⇒ "all current" signal.
      allFresh: freshCount === totalCount,
    };
  }, [sorted]);

  // Iter-277.A (Audit P3 #22) — Reset-filters helper + active-filter
  // detector. Used by the empty-state card (below) so visitors who
  // squeeze the table to zero rows via the filter chips get a clear
  // recovery CTA instead of a silent disappear.
  const hasActiveUserFilters =
    creditFilter !== "any" ||
    minRating > 0 ||
    highlightTag !== "any" ||
    driverTag !== "any" ||
    stateCode != null;
  const resetUserFilters = () => {
    setCreditFilter("any");
    setMinRating(0);
    setHighlightTag("any");
    setDriverTag("any");
    setStateCode(null);
  };

  if (!loading && sorted.length === 0) {
    // When the user has zoomed the filters down to zero rows we owe
    // them an explicit recovery path. When the underlying rates list
    // itself is empty (cold-load failure / no published rows) we
    // still hide silently — the surrounding editorial is enough.
    if (rates.length > 0 && hasActiveUserFilters) {
      const config = getCategoryConfig(category);
      const noun = config.category === "insurance"
        ? "carriers"
        : config.category === "warranty"
        ? "providers"
        : "lenders";
      return (
        <section
          data-testid="lender-rate-table"
          data-state="empty-filtered"
          data-category={category}
          className="my-12"
        >
          <div
            data-testid="lender-rate-empty-state"
            className="border border-slate-200 bg-slate-50 p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-1">
                No matches
              </p>
              <p className="font-display font-black text-lg lg:text-xl text-slate-950 leading-tight mb-1">
                No {noun} match your current filters.
              </p>
              <p className="text-sm text-slate-600 max-w-xl">
                {getEmptyStateHintCopy(category)}
              </p>
            </div>
            <button
              type="button"
              onClick={resetUserFilters}
              data-testid="lender-rate-empty-state-reset"
              className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold text-sm h-11 px-5 transition-colors"
            >
              Reset filters
            </button>
          </div>
        </section>
      );
    }
    return null;
  }

  const config = getCategoryConfig(category);
  const isCreditVertical = category === "auto-loan" || category === "refinance";
  // Iter-183.2 — Insurance gets a dedicated 6-column layout with
  // Full Coverage + Liability-only premium columns (data sourced from
  // Quadrant Information Services 2024 NAIC-derived averages). This
  // brings the table in line with NerdWallet / Bankrate's standard
  // insurance comparison pattern.
  // (isInsurance declared earlier — see useMemo dependency note.)

  return (
    <section
      data-testid="lender-rate-table"
      data-suppress-floating-proof
      data-category={category}
      className="my-12"
    >
      {datasetLd && (
        <script
          type="application/ld+json"
          // Iter-185.BF — Dataset envelope; mainEntity preserves the
          // iter-185.AU ItemList contract so historical regex tests
          // continue to pass. Two testids surfaced so legacy
          // gates + new Dataset gates both pin the same element.
          data-testid="lender-rate-table-dataset-ld"
          data-legacy-testid="lender-rate-table-itemlist-ld"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(datasetLd) }}
        />
      )}
      {productLd && (
        <script
          type="application/ld+json"
          // Iter-276.A (audit Top-5 #1) — Per-row Product+Offer+Review
          // graph. Unlocks the "Top picks" SERP carousel rich result
          // on head-term comparison queries. Distinct from the Dataset
          // envelope above (which targets AI Overview) — both ship.
          data-testid="lender-rate-table-product-ld"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(productLd) }}
        />
      )}
      {/* Iter-277.A (Audit P3 #24) — Speakable JSON-LD on the table
          title + carrier-count caption so voice assistants (Google
          Assistant, Alexa) can read the comparison headline + scope
          out loud. Targets the `[data-speakable]` selectors we wire
          on the H2 + caption pair below. No third-party dependency;
          purely a Schema.org WebPage.speakable hint. */}
      <script
        type="application/ld+json"
        data-testid="lender-rate-table-speakable-ld"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: [
                '[data-speakable="table-title"]',
                '[data-speakable="table-caption"]',
              ],
            },
          }),
        }}
      />
      <AffiliateDisclosure variant="banner" className="mb-5" />
      {/* Iter-181-F — Audited rubric trust badge. Deep-links the
          vertical-specific methodology page so readers + affiliate-network
          reviewers can audit the weights one click from the comparison.
          Iter-196.RR — Suppressed when nested inside an editorial wrapper
          (compactHeader=true) since the wrapper already surfaces the
          methodology via <CompactRubricStrip>.
          Iter-237 — Also suppressed via `suppressMethodologyBadge` when
          the parent page renders the newer rubric strip alongside the
          full header (e.g. /auto-insurance) so the legacy pill doesn't
          duplicate the new strip. */}
      {!compactHeader && !suppressMethodologyBadge && (
        <div className="mb-4">
          <MethodologyBadge category={category} />
        </div>
      )}
      {!compactHeader && (
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {/* Iter-237 — Duplicate "Updated <date>" eyebrow removed when
                the parent surface renders the newer rubric strip. The
                provenance stamp below ("Data last reviewed …") already
                ships the same date in a richer form (linked + <time>
                tag), so we only display ONE date to avoid the audit-
                flagged duplication. */}
            {!suppressMethodologyBadge && (
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#10B981]">
                {category === "insurance" || category === "warranty" ? "Updated" : "Rates as of"}{" "}
                {asOf
                  ? asOf.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "today"}
              </p>
            )}
            <SocialProofBadge category={category} />
          </div>
          <h2
            data-speakable="table-title"
            data-testid="lender-rate-table-title"
            className="font-display font-black text-3xl md:text-4xl tracking-tighter text-slate-950 leading-[1.05]"
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-base text-slate-600 mt-2 max-w-2xl">{subtitle}</p>
          )}
          {/* Iter-184.1 — "X carriers compared" trust caption. Anchors the
              scope of the comparison + the freshness of the data without
              forcing the reader to scan the methodology footer. Matches
              the NerdWallet "Compare 27 companies" affordance. */}
          {!loading && rates.length > 0 && (
            <p
              data-testid="lender-rate-carrier-count"
              data-speakable="table-caption"
              className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mt-3 flex flex-wrap items-center gap-x-2 gap-y-1"
            >
              <span>
                Comparing {rates.length} {getAuditedNoun(category)}
              </span>
              {asOf && (
                <span className="text-slate-500">
                  ·{" "}
                  {getVerifiedNoun(category)} verified{" "}
                  {asOf.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              )}
            </p>
          )}
          {/* Iter-185.BF — E-E-A-T provenance stamp paired with the new
              Dataset JSON-LD envelope. Mirrors the iter-185.BA pattern
              from the APR chart so AI-Overview engines see a HUMAN-
              readable last-reviewed date adjacent to the machine-
              readable `dateModified`. Source link routes the reader
              to the per-vertical editorial methodology. */}
          {!loading && rates.length > 0 && asOf && (
            <p
              data-testid="lender-rate-dataset-provenance"
              className="text-[11px] text-slate-500 mt-2 leading-relaxed"
            >
              Data last reviewed{" "}
              <time
                dateTime={asOf.toISOString().slice(0, 10)}
                className="font-semibold text-slate-700"
              >
                {asOf.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </time>
              . Source:{" "}
              <Link
                to={getMethodologyTarget(category)?.path || "/methodology"}
                data-testid="lender-rate-dataset-source-link"
                className="underline underline-offset-2 decoration-slate-300 hover:text-slate-950 hover:decoration-slate-950"
              >
                CarSavr editorial methodology
              </Link>
              .
            </p>
          )}
        </div>
        {/* Iter-190.J — User feedback: the "Free to compare · No
            obligation" pill at the top of the chart competed with
            the H2 + subtitle and added clutter. Removed entirely.
            The methodology link continues to satisfy the affiliate-
            network E-E-A-T requirement. */}
        <div className="flex items-center gap-3 text-xs">
          <Link
            to={getMethodologyTarget(category)?.path || "/methodology"}
            data-testid="lender-rate-methodology-link"
            className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 hover:text-slate-950 transition-colors"
          >
            <BookOpenCheck className="w-3 h-3" />
            How we score
          </Link>
        </div>
      </header>
      )}

      {/* Iter-184.1 — Sticky sort + filter bar. Holds the sort dropdown
          + insurance filter strip (state selector / driver-tag) + the
          mobile coverage-level toggle. Sticks to the top of the
          viewport as the user scrolls long tables (matches NerdWallet /
          Bankrate UX on the company comparison surface). The semi-
          opaque backdrop-blur lets the row underneath bleed through
          ~15% so it doesn't feel like a hard cut. */}
      <div
        data-testid="lender-rate-sticky-bar"
        // Iter-276.D (Top-20 #8) — Mobile sort+filter retention.
        // Pre-iter-276.D the bar used `flex-wrap`, which on mobile
        // (<768px) stacked the filter chips, coverage toggle, freshness
        // chip, refinance APR input, and sort dropdown onto 3-5 vertical
        // rows — pushing the first row of the rate table below the
        // iPhone fold (~600px) on top of the sticky bar overhead.
        //
        // Fix: mobile renders the bar as a single horizontal-scroll
        // strip (`flex-nowrap overflow-x-auto`) with each child
        // `shrink-0` so the chips keep their natural width and the
        // user swipes horizontally to reveal the rest. Desktop (md+)
        // restores the wrap behavior because the wide viewport doesn't
        // need the scroll affordance. The `[scrollbar-width:none]`
        // utility hides the native scrollbar visually but keeps the
        // scroll affordance (matches the iOS-style chip-row pattern).
        className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 mb-4 bg-white/95 backdrop-blur-sm border-y border-slate-200 flex flex-nowrap md:flex-wrap items-center gap-2 text-xs overflow-x-auto md:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {/* Mobile coverage-level toggle — insurance only. Saves the
            horizontal scroll on narrow screens by hiding one premium
            column at a time. Desktop renders both columns regardless. */}
        {isInsurance && (
          <div
            data-testid="insurance-coverage-toggle"
            className="md:hidden shrink-0 inline-flex border border-slate-300 overflow-hidden"
            role="radiogroup"
            aria-label="Coverage level"
          >
            {[
              { id: "full", label: "Full" },
              { id: "liability", label: "Liability" },
            ].map((opt) => {
              const active = coverageView === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setCoverageView(opt.id)}
                  data-testid={`insurance-coverage-toggle-${opt.id}`}
                  className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                    active
                      ? "bg-slate-950 text-white"
                      : "bg-white text-slate-600 hover:text-slate-950"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Insurance-specific filter: state selector + driver-profile chips. */}
        {isInsurance && (
          <div
            data-testid="insurance-filter-strip"
            // Iter-276.D — `shrink-0` keeps the filter strip from
            // collapsing inside the parent horizontal-scroll bar on
            // mobile; `flex-nowrap` on mobile keeps the two selects
            // (state + driver tag) on the same horizontal line so
            // the user swipes once to reveal them, not twice.
            className="shrink-0 flex flex-nowrap md:flex-wrap items-center gap-2"
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mr-1 hidden sm:inline">
              Personalize:
            </span>
            <select
              value={stateCode || ""}
              onChange={(e) => setStateCode(e.target.value || null)}
              data-testid="insurance-filter-state"
              aria-label="Filter insurance rates by state"
              className="bg-white border border-slate-300 text-slate-950 font-semibold px-2 py-1.5 outline-none focus:border-slate-950"
            >
              <option value="">National avg</option>
              {Object.keys(stateMultipliers).sort().map((s) => {
                // Iter-185.W — Precompute the option label as a single
                // string to avoid React hydration warnings. Multiple
                // adjacent JSX expressions inside the option body get
                // wrapped by the build-time source-tracker in
                // span-style elements, which produces invalid HTML
                // nesting (`span` cannot be a child of `option`) and
                // logs a React hydration warning. A single-expression
                // child renders as a pure text node — no wrapping,
                // no warning.
                const pct = Math.round((stateMultipliers[s] - 1) * 100);
                const sign = pct > 0 ? "+" : "";
                const label = `${s} · ${sign}${pct}%`;
                return (
                  <option key={s} value={s}>{label}</option>
                );
              })}
            </select>
            <select
              value={driverTag}
              onChange={(e) => setDriverTag(e.target.value)}
              data-testid="insurance-filter-driver-tag"
              aria-label="Filter insurance rates by driver profile"
              className="bg-white border border-slate-300 text-slate-950 font-semibold px-2 py-1.5 outline-none focus:border-slate-950"
            >
              <option value="any">All drivers</option>
              <option value="good-credit">Good credit</option>
              <option value="military">Military / veteran</option>
              <option value="high-risk">High-risk / DUI</option>
              <option value="low-mileage">Low mileage</option>
              <option value="bundling">Bundling discount</option>
              <option value="accident-forgiveness">Accident forgiveness</option>
              <option value="new-car-replacement">New-car replacement</option>
              <option value="rideshare">Rideshare driver</option>
              <option value="custom-coverage">Custom coverage</option>
            </select>
          </div>
        )}

        {/* Iter-185.AE (T3 + C2 from audit) — Active state-aware
            indicator. Surfaces "Showing rates for [STATE]" prominently
            when a state filter is active so visitors immediately
            understand the premium values they're scanning are
            geo-personalized (not national averages). Pulled from
            stateMultipliers metadata + the user-selected stateCode.
            Brand-emerald background reinforces this is intentional. */}
        {isInsurance && stateCode && stateMultipliers[stateCode] != null && (
          <div
            data-testid="insurance-state-indicator"
            className="shrink-0 flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs font-bold whitespace-nowrap"
          >
            <span aria-hidden className="w-1.5 h-1.5 bg-[#10B981] rounded-full" />
            <span>
              Showing rates for{" "}
              <span data-testid="insurance-state-indicator-state">{stateCode}</span>
              {(() => {
                const pct = Math.round((stateMultipliers[stateCode] - 1) * 100);
                if (pct === 0) return " (at national avg)";
                const sign = pct > 0 ? "+" : "";
                return ` (${sign}${pct}% vs national avg)`;
              })()}
            </span>
          </div>
        )}

        {/* Iter-276.A (audit Top-5 #5) + Iter-276.C — In-table freshness chip.
            Prefers the per-row data signal (`freshnessStats`, derived
            from each row's `updated_at` editorial review timestamp)
            when available; falls back to the table-wide `asOf` build
            stamp when no row-level timestamps exist. The per-row
            signal is a meaningful E-E-A-T win — competitors (NerdWallet
            / Bankrate) surface table-wide stamps; we surface "8 of 10
            carriers reviewed in the last 7 days" which no competitor
            has shipped. Closes the trust gap surfaced in the
            comparison-table audit. */}
        {(freshnessStats || asOf) && (() => {
          const useRowStats = !!freshnessStats;
          const headlineIso = useRowStats
            ? freshnessStats.freshestIso
            : asOf.toISOString();
          const headlineLabel = useRowStats
            ? freshnessStats.allFresh
              ? `All ${freshnessStats.totalCount} reviewed within 7 days`
              : `${freshnessStats.freshCount} of ${freshnessStats.totalCount} reviewed within 7 days`
            : `Rates updated ${relativeStamp(headlineIso)}`;
          const tooltip = useRowStats
            ? `Freshest row reviewed ${relativeStamp(freshnessStats.freshestIso)} · oldest row reviewed ${relativeStamp(freshnessStats.oldestIso)} · sourced from CarSavr editorial methodology`
            : `Pricing refreshed ${relativeStamp(headlineIso)} · sourced from CarSavr editorial methodology`;
          return (
            <span
              data-testid="lender-rate-freshness-chip"
              data-source={useRowStats ? "per-row" : "table-asof"}
              className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 text-emerald-900 text-[10px] font-bold uppercase tracking-[0.14em] whitespace-nowrap"
              title={tooltip}
            >
              <span aria-hidden className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-pulse" />
              {useRowStats ? (
                <span data-testid="lender-rate-freshness-row-summary">{headlineLabel}</span>
              ) : (
                <span>Rates updated {relativeStamp(headlineIso)}</span>
              )}
            </span>
          );
        })()}

        {/* Iter-196.SS — Refinance current-APR input. Drives the
            per-row "Save $XX/mo" delta column. Defaults to 10.0%
            (Experian Q3 2024 composite average for outstanding U.S.
            auto loans). Renders only on the refinance vertical so
            auto-loan-new shoppers aren't asked about a loan they
            don't yet have. */}
        {isRefinance && (
          <label
            data-testid="refinance-current-apr-control"
            className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-900 whitespace-nowrap"
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.16em]">
              Your current APR
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              max="30"
              value={currentApr}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!Number.isNaN(v) && v >= 0 && v <= 30) setCurrentApr(v);
                else if (e.target.value === "") setCurrentApr(0);
              }}
              data-testid="refinance-current-apr-input"
              aria-label="Your current auto-loan APR"
              className="w-16 bg-white border border-emerald-300 text-slate-950 font-bold px-1.5 py-1 tabular-nums outline-none focus:border-emerald-700"
            />
            <span className="text-[11px] font-semibold tabular-nums">%</span>
          </label>
        )}

        {/* Iter-276.E (Top-20 #13) — Lowest-APR / Cheapest-first preset
            chip. Visitors who skip the sort dropdown can one-tap their
            way to the cheapest row at the top. Wired to the same
            `setSortBy` so the URL params + analytics stay consistent
            with the dropdown path. Only renders when the current
            vertical's sort options actually include the lowest-APR /
            lowest-premium variant (credit + insurance verticals). */}
        {(() => {
          // Pick the most relevant "cheapest" sort id per category.
          const cheapestId = isInsurance
            ? "lowest-premium"
            : isCreditVertical
            ? "lowest-apr"
            : null;
          if (!cheapestId) return null;
          if (!config.sortOptions.includes(cheapestId)) return null;
          const isActive = sortBy === cheapestId;
          return (
            <button
              type="button"
              data-testid="lender-rate-sort-preset-cheapest"
              data-active={isActive ? "true" : "false"}
              onClick={() =>
                setSortBy(isActive ? "featured" : cheapestId)
              }
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] whitespace-nowrap border transition-colors ${
                isActive
                  ? "bg-slate-950 text-white border-slate-950"
                  : "bg-white text-slate-700 border-slate-300 hover:border-slate-950"
              }`}
              aria-pressed={isActive}
              title={
                isActive
                  ? "Cheapest-first sort active — tap to clear"
                  : isInsurance
                  ? "Sort by lowest annual premium"
                  : "Sort by lowest starting APR"
              }
            >
              <ArrowUpDown className="w-3 h-3" strokeWidth={2.5} aria-hidden />
              {isActive
                ? "Cheapest first"
                : isInsurance
                ? "Show cheapest first"
                : "Show lowest APR"}
            </button>
          );
        })()}

        {/* Sort dropdown — always shown. Spacer pushes it right on wide screens.
            Iter-276.D — `shrink-0` keeps the sort dropdown from
            collapsing inside the parent horizontal-scroll bar on
            mobile; `sm:ml-auto` still pushes it to the right edge on
            desktop. */}
        <div className="shrink-0 flex items-center gap-2 sm:ml-auto whitespace-nowrap">
          <ArrowUpDown className="w-3.5 h-3.5 text-slate-500" aria-hidden />
          <label
            htmlFor="rate-sort"
            className="text-slate-500 font-medium hidden sm:inline"
          >
            Sort:
          </label>
          <select
            id="rate-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="lender-rate-sort"
            className="bg-white border border-slate-300 text-slate-950 font-semibold px-2 py-1.5 outline-none focus:border-slate-950"
          >
            {SORT_OPTIONS.filter((o) => config.sortOptions.includes(o.id)).map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Iter-63: User filter chips. Opt-in via enableFilters prop. Shown
          only when there's meaningful filterable data and at least one
          row would survive the filter to avoid an empty-state.
          Iter-276.D (Top-20 #8) — Mobile retention: chips render as a
          single horizontal-scroll strip on mobile so the filter
          affordance survives the card-mode breakpoint switch. */}
      {enableFilters && rates.length > 3 && (
        <div
          data-testid="lender-rate-filters"
          className="flex flex-nowrap md:flex-wrap items-center gap-2 mb-5 text-xs overflow-x-auto md:overflow-visible -mx-4 sm:-mx-6 lg:-mx-0 px-4 sm:px-6 lg:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {isCreditVertical && (
            <FilterSelect
              testid="filter-credit"
              label="Credit"
              value={creditFilter}
              onChange={setCreditFilter}
              options={[
                { value: "any", label: "Any credit" },
                { value: "excellent-720+", label: "Excellent (720+)" },
                { value: "good-660-719", label: "Good (660–719)" },
                { value: "fair-580-659", label: "Fair (580–659)" },
                { value: "subprime-<580", label: "Subprime (<580)" },
              ]}
            />
          )}
          <FilterSelect
            testid="filter-rating"
            label="Min rating"
            value={String(minRating)}
            onChange={(v) => setMinRating(Number(v))}
            options={[
              { value: "0", label: "Any rating" },
              { value: "4", label: "4.0+ ★" },
              { value: "4.3", label: "4.3+ ★" },
              { value: "4.5", label: "4.5+ ★" },
            ]}
          />
          {highlightOptions.length > 1 && (
            <FilterSelect
              testid="filter-highlight"
              label="Best for"
              value={highlightTag}
              onChange={setHighlightTag}
              options={[
                { value: "any", label: "All categories" },
                ...highlightOptions.map((h) => ({ value: h, label: h })),
              ]}
            />
          )}
          {(creditFilter !== "any" || minRating > 0 || highlightTag !== "any") && (
            <button
              type="button"
              onClick={() => {
                setCreditFilter("any");
                setMinRating(0);
                setHighlightTag("any");
              }}
              data-testid="filter-clear"
              className="min-h-[36px] text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 hover:text-slate-950 transition-colors px-2"
            >
              Clear filters
            </button>
          )}
          <span
            data-testid="filter-result-count"
            className="ml-auto text-[11px] font-semibold text-slate-500"
          >
            Showing {sorted.length} {sorted.length === 1 ? "match" : "matches"}
          </span>
        </div>
      )}

      {/* Iter-185.B (Sprint B · audit item M4) — Loading skeleton.
          Before this fix, the table briefly rendered nothing during
          the initial fetch (~400ms on cold load). Bankrate / NerdWallet
          / The Zebra all render a pulsing skeleton row stack to anchor
          layout + perceived performance. The skeleton mirrors the row
          count (5) + column count of the active vertical so the first
          paint doesn't shift when real data lands.

          Suppressed on re-fetches (e.g., state filter change) — only
          fires on initial cold mount when sorted.length === 0. */}
      {loading && sorted.length === 0 && (
        // Iter-276.E (Top-20 #14) — Centralized skeleton primitive.
        // Pre-iter-276.E the 150-line inline skeleton lived here;
        // hoisted to `TableSkeleton.jsx` so the other 26 comparison
        // surfaces can drop in the same loading-state language with
        // one import. The desktop column count + mobile row count
        // pass through so warranty (3 verticals) and refi (8 columns)
        // both get appropriately-sized placeholders.
        <TableSkeleton
          rows={5}
          mobileRows={3}
          columns={isCreditVertical ? (isRefinance ? 8 : 7) : isInsurance ? 6 : 5}
          variant="rate"
          testid="lender-rate-table-skeleton"
        />
      )}

      {/* Iter-276.A (audit P0 + Top-5 #4) — Mobile above-the-fold
          Top Pick strip. Surfaces the #1 row's Apply CTA in a tight
          compact row immediately below the sticky sort bar so on a
          standard iPhone (375×667 hero ≈ 600px) the affiliate CTA
          stays visible above y≈600 fold, instead of being pushed to
          y≈900 inside the first mobile card. Renders only on:
            • mobile (md:hidden)
            • default "featured" sort (so the strip stays consistent
              with the rank-1 row below; we don't show "Top pick" when
              the user has re-sorted by lowest APR / highest rated)
            • Non-loading state with at least one row
          Tapping the CTA fires the same affiliate / fallback click
          handler as the first card row — no duplicate analytics path. */}
      {!loading && sorted.length > 0 && sortBy === "featured" && (() => {
        const top = sorted[0];
        if (!top) return null;
        const aprLabel = top.min_apr != null ? formatApr(top.min_apr, top.max_apr) : null;
        const premiumLabel =
          top.avg_annual_premium_full != null
            ? `$${Math.round(top.avg_annual_premium_full / 12)}/mo`
            : null;
        const priceLabel =
          top.price_low != null
            ? `$${top.price_low}${top.price_high ? `–$${top.price_high}` : ""}`
            : null;
        const headlineValue = aprLabel || premiumLabel || priceLabel || null;
        const headlineLabel = aprLabel
          ? "Starting APR"
          : premiumLabel
          ? "Avg full coverage"
          : priceLabel
          ? "Coverage from"
          : null;
        return (
          <div
            data-testid="lender-rate-mobile-top-pick"
            className="md:hidden mb-4 border-2 border-[#10B981] bg-gradient-to-br from-emerald-50 to-white p-3 flex items-center gap-3"
          >
            <span
              aria-hidden
              className="inline-flex items-center justify-center w-9 h-9 bg-[#10B981] text-white font-display font-black text-base shrink-0"
            >
              #1
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-black uppercase tracking-[0.18em] text-[#10B981] mb-0.5 flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5" strokeWidth={3} aria-hidden />
                Editor's pick · 2-min compare
              </p>
              <p
                data-testid="lender-rate-mobile-top-pick-name"
                className="font-display font-black text-[15px] text-slate-950 leading-tight truncate"
              >
                {top.lender_name || top.name || top.lender_slug}
              </p>
              {headlineValue && headlineLabel && (
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600 mt-0.5">
                  {headlineLabel}{" "}
                  <span className="text-slate-950 tabular-nums">{headlineValue}</span>
                </p>
              )}
            </div>
            <div className="shrink-0">
              <ApplyButton
                rate={top}
                sourcePage={sourcePage}
                config={config}
                testIdSuffix="-mobile-top-pick"
                rankMeta={{ rank: 1, isEditorsPick: true, sortMode: sortBy }}
              />
            </div>
          </div>
        );
      })()}

      {/* Iter-276.F (Top-20 #17 a11y "potential improvement") —
          aria-live region for sort-change announcements. When the
          visitor toggles the sort (via the dropdown, a column header,
          or the new "Cheapest first" preset chip), the assistive tech
          gets a polite spoken update ("Sorted by lowest APR" /
          "Sorted by highest rated", etc.). Hidden visually via the
          standard sr-only class — the announcement only surfaces to
          screen readers. WAI-ARIA "Sortable Table" pattern §6.6.7
          recommendation. */}
      <div
        data-testid="lender-rate-sort-announcement"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {(() => {
          const sortLabelMap = {
            featured: "default editor's pick order",
            "lowest-apr": "lowest starting APR first",
            "lowest-premium": "lowest annual premium first",
            "lowest-premium-full": "lowest full-coverage premium first",
            "lowest-premium-liability": "lowest liability-only premium first",
            "lowest-credit": "lowest minimum credit-score floor first",
            "highest-rated": "highest editor score first",
          };
          const verticalName = isInsurance
            ? "carriers"
            : category === "warranty"
            ? "providers"
            : "lenders";
          const what =
            sortLabelMap[sortBy] ||
            (sortBy ? sortBy.replace(/-/g, " ") : "default order");
          // Iter-423 — never emit "0 carriers shown" before data loads.
          if (!rates.length) return "";
          if (!sorted.length) return `No ${verticalName} match these filters.`;
          return `${sorted.length} ${verticalName} shown, sorted by ${what}.`;
        })()}
      </div>

      {/* Iter-189.5 Sprint 4.5 — Insurance marketplaces split into a
          dedicated strip above the direct-carrier table. Eliminates
          the empty-premium-cell trust hit on rows 1+2. */}
      {isInsurance && marketplaceRows.length > 0 && (
        <MarketplaceStrip rows={marketplaceRows} sourcePage={sourcePage} />
      )}

      {/* Iter-195.B (T7) — Multi-quote callout above the table for
          non-insurance verticals (insurance already has the dedicated
          MarketplaceStrip above). Mirrors the LendingTree pattern of
          leading with a compare-many-lenders CTA before showing the
          per-lender comparison rows. */}
      {!isInsurance && !loading && sorted.length > 0 && (
        <MultiQuoteCallout
          category={category}
          onCompare={() => {
            const modalCategory = CATEGORY_TO_MODAL[category] || category;
            track(events.MULTI_QUOTE_CALLOUT_CLICK || "multi_quote_callout_click", {
              page_location: sourcePage,
              offer_type: category,
            });
            openTopLevelQuoteModal(modalCategory);
          }}
        />
      )}

      {/* Desktop table — Iter-92: sticky header so the column meaning stays
          visible while the user scrolls long rate tables (premium fintech
          convention used by Bankrate / NerdWallet / Wise). Adds light shadow
          on scroll via Tailwind's sticky + z-index combo.
          Iter-189.B (audit) — Switched outer wrapper from `overflow-hidden`
          to `overflow-x-auto`. The previous setting clipped the CTA column
          off the right edge when the carrier column (logo + Editor's Pick
          + Marketplace badges) consumed too much horizontal space at
          mid-widths. The inner `<table>` now declares `min-w-[860px]` so
          all 6 insurance columns (Carrier · Full · Liability · Best for ·
          Rated · CTA) always render with breathing room; the wrapper
          scrolls horizontally on narrower breakpoints instead of cropping. */}
      <div className="hidden md:block border border-slate-200 bg-white overflow-x-auto">
        <table
          // Iter-276.F (Top-20 #17 a11y) — Explicit `role="table"` +
          // `aria-label` for screen-reader announcements. The aria-label
          // is category-specific so the SR user knows which rate-table
          // they've landed on without having to navigate back to the
          // page H1. WAI-ARIA 1.2 §6.6 "Sortable Table" pattern.
          role="table"
          aria-label={
            isInsurance
              ? "Auto insurance carrier comparison — sortable by rating and premium"
              : isRefinance
              ? "Auto refinance lender comparison — sortable by APR, term, credit floor, and rating"
              : isCreditVertical
              ? "Auto loan lender comparison — sortable by APR, term, credit floor, and rating"
              : "Extended warranty provider comparison — sortable by rating"
          }
          className="w-full text-sm min-w-[860px]"
        >
          {/* Iter-190.J — Beefed-up header row.
              User feedback: column labels read cramped and wrapped
              onto 2 lines ("FULL / COVERAGE", "LIABILITY- / ONLY").
              Bumped to text-xs (12px) + font-black, padded py-4,
              tightened letter-spacing to 0.10em so the labels never
              wrap. Darkened to text-slate-700 so the row reads as a
              proper header band, not muted secondary text. */}
          <thead className="bg-slate-50 border-b-2 border-slate-200 sticky top-0 z-10">
            <tr className="text-left text-xs uppercase tracking-[0.10em] font-black text-slate-700">
              {isCreditVertical ? (
                <>
                  <th aria-sort="none" className="px-5 py-4 whitespace-nowrap">Lender</th>
                  <SortableHeader
                    id="lowest-apr"
                    label="APR"
                    align="right"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleColumnSort}
                    className="text-right tabular-nums whitespace-nowrap"
                    testid="sortable-header-apr"
                  />
                  {/* Iter-196.RR — Est. payment column. Standardized
                      $30k / 60mo math so every APR translates into an
                      apples-to-apples $$/mo figure pre-click. Per Bankrate
                      2024 telemetry this is the #2 affiliate-CTR lever
                      after positioning the table above the fold. */}
                  <th
                    className="px-5 py-4 text-right tabular-nums whitespace-nowrap hidden lg:table-cell"
                    data-testid="column-est-payment"
                  >
                    Est. payment
                  </th>
                  {/* Iter-196.SS — Savings-vs-current-loan column.
                      Refinance only — auto-loan-new shoppers don't
                      have an existing loan to beat. Hidden below lg
                      so the narrow-viewport hierarchy stays Lender →
                      APR → Min credit → Apply. */}
                  {isRefinance && (
                    <th
                      className="px-5 py-4 text-right tabular-nums whitespace-nowrap hidden lg:table-cell"
                      data-testid="column-refi-savings"
                    >
                      Savings vs current
                    </th>
                  )}
                  <SortableHeader
                    id="lowest-credit"
                    label="Min. credit score"
                    align="right"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleColumnSort}
                    className="text-right tabular-nums whitespace-nowrap"
                    testid="sortable-header-min-credit"
                  />
                  <th aria-sort="none" className="px-5 py-4 text-right tabular-nums whitespace-nowrap">Loan amount</th>
                  <th aria-sort="none" className="px-5 py-4 text-right tabular-nums whitespace-nowrap">Loan length</th>
                  <SortableHeader
                    id="highest-rated"
                    label="Editor score"
                    align="right"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleColumnSort}
                    className="text-right whitespace-nowrap"
                    testid="sortable-header-rated"
                  />
                  <th aria-sort="none" className="px-5 py-4 text-right" aria-label="Apply"></th>
                </>
              ) : isInsurance ? (
                <>
                  <th aria-sort="none" className="px-5 py-4 whitespace-nowrap">Carrier</th>
                  <SortableHeader
                    id="lowest-premium-full"
                    label="Full coverage"
                    align="left"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleColumnSort}
                    className={`text-left tabular-nums whitespace-nowrap ${
                      coverageView === "liability" ? "hidden md:table-cell" : ""
                    }`}
                    testid="sortable-header-full-coverage"
                  />
                  <SortableHeader
                    id="lowest-premium-liability"
                    label="Liability-only"
                    align="left"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleColumnSort}
                    className={`text-left tabular-nums whitespace-nowrap ${
                      coverageView === "full" ? "hidden md:table-cell" : ""
                    }`}
                    testid="sortable-header-liability"
                  />
                  <th aria-sort="none" className="px-5 py-4 hidden md:table-cell whitespace-nowrap">Best for</th>
                  <SortableHeader
                    id="highest-rated"
                    label="Editor score"
                    align="right"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleColumnSort}
                    className="text-right whitespace-nowrap"
                    testid="sortable-header-insurance-rated"
                  />
                  <th aria-sort="none" className="px-5 py-4 text-right" aria-label="Apply"></th>
                </>
              ) : (
                <>
                  <th aria-sort="none" className="px-5 py-4 whitespace-nowrap">
                    {category === "warranty" ? "Provider" : "Carrier"}
                  </th>
                  <th aria-sort="none" className="px-5 py-4 whitespace-nowrap">Best for</th>
                  <th aria-sort="none" className="px-5 py-4 whitespace-nowrap">Why we picked it</th>
                  <SortableHeader
                    id="highest-rated"
                    label="Editor score"
                    align="right"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleColumnSort}
                    className="text-right whitespace-nowrap"
                    testid="sortable-header-warranty-rated"
                  />
                  <th aria-sort="none" className="px-5 py-4 text-right" aria-label="Apply"></th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {displayRows.map((r, idx) => {
              const isOpen = expandedSlug === r.slug;
              const isFirstRow = idx === 0;
              // Iter-148 — Unified table polish. Top 3 rows show a rank
              // chip in the lender cell so the table reads as a
              // leaderboard (matching Bankrate / NerdWallet pattern).
              // Rank only renders for the default "featured" sort to
              // avoid implying "best to worst" when the user has sorted
              // by lowest APR / lowest credit floor / highest rated.
              const showRank = sortBy === "featured" && idx < 3;
              // The #1 row also gets a subtle emerald-tinted background
              // accent + an "Editor's Pick" microbadge as the visual
              // anchor of the table.
              const isEditorsPick = sortBy === "featured" && idx === 0;
              return (
                <React.Fragment key={r.slug}>
                  <tr
                    data-testid={`rate-row-${r.slug}`}
                    data-editors-pick={isEditorsPick || undefined}
                    className={`rate-row group hover:bg-slate-50/60 transition-colors relative ${
                      isEditorsPick ? "bg-[#10B981]/[0.04]" : ""
                    }`}
                  >
                    {isCreditVertical ? (
                      <>
                        <td className="px-5 py-4 relative">
                          {/* Iter-148 — left-edge emerald accent bar on hover; persistent on the Editor's Pick row */}
                          <span
                            aria-hidden
                            className={`absolute left-0 top-3 bottom-3 w-[3px] bg-[#10B981] transition-opacity ${
                              isEditorsPick ? "opacity-100" : "opacity-0 group-hover:opacity-60"
                            }`}
                          />
                          <div className="flex items-start gap-3">
                            {showRank && (
                              <span className="inline-flex items-start gap-0.5">
                                <RankChip
                                  rank={idx + 1}
                                  isPick={isEditorsPick}
                                  slug={r.slug}
                                />
                                {/* Iter-195.B (T10) — Per-row "Why this rank?" tooltip. */}
                                <WhyRankTooltip
                                  rate={r}
                                  rank={idx + 1}
                                  category={category}
                                  slug={r.slug}
                                />
                              </span>
                            )}
                            <div className="min-w-0">
                              <div className="leading-tight">
                                <CarrierLogo slug={r.slug} name={r.name} size="uniform" />
                              </div>
                              {/* Iter-185.AF + Iter-185.BK +
                                  Iter-190.I — Unified trust strip.
                                  AM Best (loans + insurance), J.D.
                                  Power (insurance only), NAIC
                                  complaint index (insurance only)
                                  all render on a single horizontal
                                  row beneath the carrier name with
                                  `·` sibling separators. Each badge
                                  returns null when its source data
                                  isn't set, so the row collapses
                                  gracefully on uncovered carriers /
                                  non-insurance rows. ~30% vertical
                                  cleanup vs. the prior 2-row stack. */}
                              <div
                                data-testid={`trust-strip-${r.slug}`}
                                className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[10px] font-bold uppercase tracking-[0.14em] [&>*+*]:before:content-['·'] [&>*+*]:before:text-slate-300 [&>*+*]:before:mr-2 [&>*+*]:before:font-medium"
                              >
                                <AmBestBadge slug={r.slug} />
                                {category === "insurance" && (
                                  <TrustBadgeStack slug={r.slug} />
                                )}
                              </div>
                              {isEditorsPick && (
                                <span
                                  data-testid={`editors-pick-badge-${r.slug}`}
                                  className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#10B981]"
                                >
                                  <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                                  Editor's pick
                                </span>
                              )}
                              {r.highlight && !isEditorsPick && (
                                // Iter-276.E (Top-20 #10 + #12) — Editorial
                                // badge visual boost. Pre-iter-276.E the
                                // highlight rendered as plain emerald
                                // uppercase text which (a) competed visually
                                // with the Apply/Compare emerald CTAs (audit
                                // #12 emerald-discipline finding) and (b)
                                // lacked the visual weight to be scanned
                                // from 3 ft (audit #10). Switched to a
                                // slate-900 chip with white text + a
                                // pulsing emerald dot — bumps visual weight
                                // ~3× while reclaiming the emerald hue for
                                // the primary CTA. The Award icon paired
                                // with the chip gives the signal a second
                                // (icon-shape) layer for color-blind viewers
                                // (audit #15 a11y win). */}
                                <div
                                  data-testid={`rate-highlight-badge-${r.slug}`}
                                  className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 bg-slate-950 text-white text-[10px] font-black uppercase tracking-[0.14em]"
                                >
                                  <span
                                    aria-hidden
                                    className="w-1.5 h-1.5 bg-[#10B981] rounded-full"
                                  />
                                  <Award
                                    className="w-2.5 h-2.5 text-[#10B981]"
                                    strokeWidth={3}
                                    aria-hidden
                                  />
                                  {r.highlight}
                                </div>
                              )}
                              <CategoryDetailGlance details={r.details} category={r.category} slug={r.slug} />
                            </div>
                          </div>
                        </td>
                        <td
                          className="px-5 py-4 text-right font-display font-bold text-slate-950 whitespace-nowrap tabular-nums"
                          data-testid={`rate-apr-${r.slug}`}
                        >
                          <div>{formatApr(r.min_apr, r.max_apr)}</div>
                          {/* Iter-184 — Sample-loan total-interest sub-line.
                              Computed for a $25k/60-month loan at the
                              advertised min APR. Mirrors Bankrate's
                              "What you'd pay" anchor column. */}
                          {(() => {
                            const ti = computeTotalInterest(r.min_apr, 25000, 60);
                            if (ti == null) return null;
                            return (
                              <div className="text-[10px] font-normal text-slate-500 leading-tight mt-0.5">
                                Total int. ~${ti.toLocaleString()}
                                <span className="text-slate-500">
                                  {" "}
                                  · $25k · 60mo
                                </span>
                              </div>
                            );
                          })()}
                        </td>
                        {/* Iter-196.RR — Estimated monthly payment cell.
                            Hidden on mobile/tablet (lg:table-cell) so
                            the narrow viewport hierarchy still leads
                            with Lender → APR → Min credit → Apply.
                            On desktop, every APR earns its own $$/mo
                            translation. */}
                        <td
                          className="px-5 py-4 text-right whitespace-nowrap hidden lg:table-cell tabular-nums"
                          data-testid={`rate-est-payment-${r.slug}`}
                        >
                          {(() => {
                            const pmt = computeMonthlyPmt(r.min_apr, 30000, 60);
                            if (pmt == null) {
                              return <span className="text-slate-500">—</span>;
                            }
                            return (
                              <>
                                <div className="font-display font-bold text-slate-950">
                                  ${pmt.toLocaleString()}
                                  <span className="text-slate-500 font-normal">
                                    /mo
                                  </span>
                                </div>
                                <div className="text-[10px] font-normal text-slate-500 leading-tight mt-0.5">
                                  $30k · 60mo
                                </div>
                              </>
                            );
                          })()}
                        </td>
                        {/* Iter-196.SS — Per-row refinance savings cell.
                            Personalized to the visitor's currentApr
                            input above the table. Emerald copy on
                            positive savings; muted "—" when the
                            lender's APR doesn't beat the current
                            loan (so we never show $0 savings). */}
                        {isRefinance && (
                          <td
                            className="px-5 py-4 text-right whitespace-nowrap hidden lg:table-cell tabular-nums"
                            data-testid={`rate-refi-savings-${r.slug}`}
                          >
                            {(() => {
                              const sv = computeMonthlySavings(currentApr, r.min_apr, 30000, 60);
                              if (sv == null) {
                                return <span className="text-slate-500" data-testid={`rate-refi-savings-empty-${r.slug}`}>—</span>;
                              }
                              return (
                                <>
                                  <div className="font-display font-bold text-emerald-700">
                                    Save ${sv.monthly.toLocaleString()}
                                    <span className="text-emerald-600/70 font-normal">
                                      /mo
                                    </span>
                                  </div>
                                  <div className="text-[10px] font-normal text-emerald-700/70 leading-tight mt-0.5">
                                    ${sv.lifetime.toLocaleString()} over 60mo
                                  </div>
                                </>
                              );
                            })()}
                          </td>
                        )}
                        <td className="px-5 py-4 text-right text-slate-700 tabular-nums">{formatCredit(r.min_credit)}</td>
                        <td className="px-5 py-4 text-right text-slate-700 whitespace-nowrap tabular-nums">
                          {formatLoanRange(r.min_loan, r.max_loan)}
                        </td>
                        <td className="px-5 py-4 text-right text-slate-700 whitespace-nowrap tabular-nums">
                          {formatTerm(r.min_term_months, r.max_term_months)}
                        </td>
                      </>
                    ) : isInsurance ? (
                      <>
                        <td className="px-5 py-4 align-top relative">
                          <span
                            aria-hidden
                            className={`absolute left-0 top-3 bottom-3 w-[3px] bg-[#10B981] transition-opacity ${
                              isEditorsPick ? "opacity-100" : "opacity-0 group-hover:opacity-60"
                            }`}
                          />
                          <div className="flex items-start gap-3">
                            {showRank && (
                              <span className="inline-flex items-start gap-0.5">
                                <RankChip
                                  rank={idx + 1}
                                  isPick={isEditorsPick}
                                  slug={r.slug}
                                />
                                {/* Iter-195.B (T10) — Per-row "Why this rank?" tooltip. */}
                                <WhyRankTooltip
                                  rate={r}
                                  rank={idx + 1}
                                  category={category}
                                  slug={r.slug}
                                />
                              </span>
                            )}
                            <div className="min-w-0">
                              <div className="leading-tight">
                                <CarrierLogo slug={r.slug} name={r.name} size="uniform" />
                              </div>
                              {/* Iter-185.AF + Iter-185.BK +
                                  Iter-190.I — Unified trust strip
                                  (insurance row context). */}
                              <div
                                data-testid={`trust-strip-${r.slug}`}
                                className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[10px] font-bold uppercase tracking-[0.14em] [&>*+*]:before:content-['·'] [&>*+*]:before:text-slate-300 [&>*+*]:before:mr-2 [&>*+*]:before:font-medium"
                              >
                                <AmBestBadge slug={r.slug} />
                                {category === "insurance" && (
                                  <TrustBadgeStack slug={r.slug} />
                                )}
                              </div>
                              {isEditorsPick && (
                                <span
                                  data-testid={`editors-pick-badge-${r.slug}`}
                                  className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#10B981]"
                                >
                                  <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                                  Editor's pick
                                </span>
                              )}
                              {r.provider_type === "marketplace" && (
                                <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                                  Marketplace · 100+ carriers
                                </span>
                              )}
                              <CategoryDetailGlance details={r.details} category={r.category} slug={r.slug} />
                              {/* Iter-195.B (T4) — Coverage limits inline (BI/PD/UM). */}
                              <CoverageLimitsInline details={r.details} slug={r.slug} />
                            </div>
                          </div>
                        </td>
                        <td
                          className={`px-5 py-4 text-left align-top whitespace-nowrap tabular-nums ${
                            coverageView === "liability" ? "hidden md:table-cell" : ""
                          }`}
                          data-testid={`rate-premium-full-${r.slug}`}
                        >
                          {(() => {
                            const p = formatPremium(r.avg_annual_premium_full);
                            if (!p) {
                              return (
                                <span className="text-slate-500 text-xs">—</span>
                              );
                            }
                            return (
                              <>
                                <div className="font-display font-bold text-slate-950">
                                  ${p.monthly}/mo
                                </div>
                                <div className="text-[11px] text-slate-500">
                                  ${p.annual.toLocaleString()}/yr
                                </div>
                                {stateCode && (
                                  <div
                                    data-testid={`rate-premium-full-state-pill-${r.slug}`}
                                    className="mt-1 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] bg-[#10B981]/15 text-[#0A2540] border border-[#10B981]/30"
                                  >
                                    Avg in {stateCode}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td
                          className={`px-5 py-4 text-left align-top whitespace-nowrap tabular-nums ${
                            coverageView === "full" ? "hidden md:table-cell" : ""
                          }`}
                          data-testid={`rate-premium-liability-${r.slug}`}
                        >
                          {(() => {
                            const p = formatPremium(r.avg_annual_premium_liability);
                            if (!p) {
                              return (
                                <span className="text-slate-500 text-xs">—</span>
                              );
                            }
                            return (
                              <>
                                <div className="font-display font-bold text-slate-950">
                                  ${p.monthly}/mo
                                </div>
                                <div className="text-[11px] text-slate-500">
                                  ${p.annual.toLocaleString()}/yr
                                </div>
                                {stateCode && (
                                  <div
                                    data-testid={`rate-premium-liability-state-pill-${r.slug}`}
                                    className="mt-1 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] bg-[#10B981]/15 text-[#0A2540] border border-[#10B981]/30"
                                  >
                                    Avg in {stateCode}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td className="px-5 py-4 align-top hidden md:table-cell">
                          {(() => {
                            const label = deriveBestForLabel(r);
                            if (!label) {
                              return <span className="text-slate-500 text-xs">—</span>;
                            }
                            return (
                              <span
                                data-testid={`best-for-rotation-${r.slug}`}
                                className="inline-block text-xs text-[#0A2540] font-semibold bg-[#10B981]/15 px-2 py-1"
                              >
                                {label}
                              </span>
                            );
                          })()}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-5 py-4 align-top relative">
                          {/* Iter-148 — left-edge emerald accent bar on hover; persistent on the Editor's Pick row */}
                          <span
                            aria-hidden
                            className={`absolute left-0 top-3 bottom-3 w-[3px] bg-[#10B981] transition-opacity ${
                              isEditorsPick ? "opacity-100" : "opacity-0 group-hover:opacity-60"
                            }`}
                          />
                          <div className="flex items-start gap-3">
                            {showRank && (
                              <span className="inline-flex items-start gap-0.5">
                                <RankChip
                                  rank={idx + 1}
                                  isPick={isEditorsPick}
                                  slug={r.slug}
                                />
                                {/* Iter-195.B (T10) — Per-row "Why this rank?" tooltip. */}
                                <WhyRankTooltip
                                  rate={r}
                                  rank={idx + 1}
                                  category={category}
                                  slug={r.slug}
                                />
                              </span>
                            )}
                            <div className="min-w-0">
                              <div className="leading-tight">
                                <CarrierLogo slug={r.slug} name={r.name} size="uniform" />
                              </div>
                              {/* Iter-185.AF + Iter-185.BK +
                                  Iter-190.I — Unified trust strip
                                  (warranty / hybrid row context). */}
                              <div
                                data-testid={`trust-strip-${r.slug}`}
                                className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[10px] font-bold uppercase tracking-[0.14em] [&>*+*]:before:content-['·'] [&>*+*]:before:text-slate-300 [&>*+*]:before:mr-2 [&>*+*]:before:font-medium"
                              >
                                <AmBestBadge slug={r.slug} />
                                {category === "insurance" && (
                                  <TrustBadgeStack slug={r.slug} />
                                )}
                              </div>
                              {isEditorsPick && (
                                <span
                                  data-testid={`editors-pick-badge-${r.slug}`}
                                  className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#10B981]"
                                >
                                  <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                                  Editor's pick
                                </span>
                              )}
                              <CategoryDetailGlance details={r.details} category={r.category} slug={r.slug} />
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 align-top">
                          {(() => {
                            const label = deriveBestForLabel(r);
                            if (!label) {
                              return <span className="text-slate-500 text-xs">—</span>;
                            }
                            return (
                              <span
                                data-testid={`best-for-rotation-${r.slug}`}
                                className="inline-block text-xs text-[#0A2540] font-semibold bg-[#10B981]/15 px-2 py-1"
                              >
                                {label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-5 py-4 align-top text-slate-700 leading-relaxed text-[13px] max-w-md">
                          {r.editor_review || (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                      </>
                    )}
                    <td className="px-5 py-4 text-right align-top">
                      <div className="inline-flex flex-col items-end">
                        <div className="inline-flex items-center">
                          <StarRating
                            value={r.rating}
                            onClick={() => toggleExpanded(r.slug)}
                            expanded={isOpen}
                            testIdSlug={r.slug}
                          />
                          {/* Iter-195.B (T1) — 4-factor breakdown popover. */}
                          <ScoreHoverCard rate={r} slug={r.slug} />
                        </div>
                        <ReviewedBadge iso={r.updated_at} slug={r.slug} />
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right align-top">
                      <ApplyButton
                        rate={r}
                        sourcePage={sourcePage}
                        config={config}
                        rankMeta={{
                          rank: showRank ? idx + 1 : null,
                          isEditorsPick,
                          sortMode: sortBy,
                        }}
                      />
                      <div className="flex justify-end">
                        <CompareToggle rate={r} showFirstScrollHint={isFirstRow} />
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr data-testid={`rate-breakdown-row-${r.slug}`}>
                      <td colSpan={isCreditVertical ? (isRefinance ? 9 : 8) : isInsurance ? 6 : 5} className="p-0">
                        <ScoreBreakdown
                          rate={r}
                          rank={showRank ? idx + 1 : null}
                          isEditorsPick={isEditorsPick}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Iter-184.1 — "See more results" expand button. Mirrors the
          NerdWallet pattern: surface the top-N rows + reveal the rest
          on demand. Each click extends `expandedLimit` by 5; hides
          once all rows are visible. Works on both desktop table +
      {/* Iter-185.AD — "See N more" toggle MOVED to after the mobile
          cards block per user audit ("On the mobile insurance
          comparison table it says (See 4 more carriers (6 of 10
          Shown) at the top, shouldn't this be at the bottom to
          expand?"). The single toggle now sits below BOTH the
          desktop table and the mobile cards block, so it lands at
          the natural "I've scanned the visible options, show me
          more" position on every layout.

          Was rendered here at lines 1396-1415 of the iter-185.AC
          contract. Now relocated to line ~1528 (after the mobile
          cards block closes). */}

      {/* Mobile card layout — iter-148: rank chip + editor's pick badge
          + lift hover state mirror the desktop table so the visual
          language stays unified across breakpoints. */}
      <div className="md:hidden space-y-3">
        {displayRows.map((r, idx) => {
          const isOpen = expandedSlug === r.slug;
          const showRank = sortBy === "featured" && idx < 3;
          const isEditorsPick = sortBy === "featured" && idx === 0;
          return (
            <article
              key={r.slug}
              data-testid={`rate-card-${r.slug}`}
              data-editors-pick={isEditorsPick || undefined}
              className={`relative border border-slate-200 bg-white transition-all hover:border-slate-300 ${
                isEditorsPick ? "ring-1 ring-[#10B981]/40" : ""
              }`}
            >
              {isEditorsPick && (
                <span
                  aria-hidden
                  className="absolute left-0 top-0 bottom-0 w-1 bg-[#10B981]"
                />
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex items-start gap-3">
                    {showRank && (
                      <RankChip
                        rank={idx + 1}
                        isPick={isEditorsPick}
                        slug={`${r.slug}-mobile`}
                      />
                    )}
                    <div className="min-w-0">
                      <div className="leading-tight">
                        <CarrierLogo slug={r.slug} name={r.name} size="uniform" />
                      </div>
                      {/* Iter-185.AF + Iter-185.BK + Iter-190.I +
                          Iter-190.K — Unified mobile trust strip.
                          User feedback (iter-190.K): the
                          `[&>*+*]:before:content-['·']` sibling-dot
                          rule was triggering a leading dot at the
                          start of wrapped lines on narrow screens
                          ("· J.D. Power 2024 853 · NAIC 1.18"
                          on its own line). Switched to a clean
                          vertical stack on mobile (flex-col +
                          gap-y-0.5) — AM Best occupies row 1, the
                          inline J.D. Power · NAIC pair sits on row 2
                          via TrustBadgeStack's internal `·`. Both
                          fit on a single line each at 360px+. */}
                      <div
                        data-testid={`trust-strip-${r.slug}-mobile`}
                        className="mt-1.5 flex flex-col items-start gap-y-0.5 text-[10px] font-bold uppercase tracking-[0.10em]"
                      >
                        <AmBestBadge slug={r.slug} />
                        {category === "insurance" && (
                          <TrustBadgeStack slug={r.slug} />
                        )}
                      </div>
                      {isEditorsPick && (
                        <span
                          data-testid={`editors-pick-badge-${r.slug}-mobile`}
                          className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#10B981]"
                        >
                          <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                          Editor's pick
                        </span>
                      )}
                      {/* Iter-195.B (T5) — Secondary details collapsed by
                          default on mobile to eliminate the wall-of-text
                          experience. Tap the "Show details" button below
                          (or the rating chevron) to reveal. */}
                      {isOpen && r.highlight && !isEditorsPick && (
                        // Iter-276.E (Top-20 #10 + #12) — Mobile badge
                        // matches the desktop slate-900 chip pattern so
                        // the visual language stays unified across
                        // breakpoints (iter-148 unification mandate).
                        <div
                          data-testid={`rate-highlight-badge-${r.slug}-mobile`}
                          className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 bg-slate-950 text-white text-[10px] font-black uppercase tracking-[0.14em]"
                        >
                          <span
                            aria-hidden
                            className="w-1.5 h-1.5 bg-[#10B981] rounded-full"
                          />
                          <Award
                            className="w-2.5 h-2.5 text-[#10B981]"
                            strokeWidth={3}
                            aria-hidden
                          />
                          {r.highlight}
                        </div>
                      )}
                      {isOpen && (
                        <CategoryDetailGlance details={r.details} category={r.category} slug={`${r.slug}-mobile`} />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end shrink-0">
                    <StarRating
                      value={r.rating}
                      onClick={() => toggleExpanded(r.slug)}
                      expanded={isOpen}
                      testIdSlug={`${r.slug}-mobile`}
                    />
                    <ReviewedBadge iso={r.updated_at} slug={`${r.slug}-mobile`} />
                  </div>
                </div>
                {isCreditVertical ? (
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-4">
                    <RateCell label="APR" value={formatApr(r.min_apr, r.max_apr)} dominant />
                    <RateCell label="Min. credit score" value={formatCredit(r.min_credit)} />
                    <RateCell label="Loan amount" value={formatLoanRange(r.min_loan, r.max_loan)} />
                    <RateCell label="Loan length" value={formatTerm(r.min_term_months, r.max_term_months)} />
                  </dl>
                ) : category === "insurance" ? (
                  /* Iter-185.AD — Premium block on the mobile insurance
                     card. User audit: "Not seeing premiums on the
                     mobile carrier comparison table." Mirrors the
                     desktop columns (full coverage + liability-only),
                     surfaced as a 2-up grid with $X/mo headline +
                     $Y/yr sub-line per NerdWallet/Bankrate convention.
                     Editor review (if present) renders below. */
                  <>
                    <dl
                      data-testid={`rate-mobile-premiums-${r.slug}`}
                      className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3 border-y border-slate-100 py-3"
                    >
                      {(() => {
                        const full = formatPremium(r.avg_annual_premium_full);
                        const lib = formatPremium(r.avg_annual_premium_liability);
                        return (
                          <>
                            <div>
                              <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-0.5">
                                Full coverage
                              </dt>
                              <dd className="tabular-nums">
                                {full ? (
                                  <>
                                    <span
                                      data-testid={`rate-mobile-premium-full-${r.slug}`}
                                      className="font-display font-bold text-slate-950 text-[15px]"
                                    >
                                      ${full.monthly}/mo
                                    </span>
                                    <div className="text-[11px] text-slate-500">
                                      ${full.annual.toLocaleString()}/yr
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-slate-500 text-xs">—</span>
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-0.5">
                                Liability-only
                              </dt>
                              <dd className="tabular-nums">
                                {lib ? (
                                  <>
                                    <span
                                      data-testid={`rate-mobile-premium-liability-${r.slug}`}
                                      className="font-display font-bold text-slate-950 text-[15px]"
                                    >
                                      ${lib.monthly}/mo
                                    </span>
                                    <div className="text-[11px] text-slate-500">
                                      ${lib.annual.toLocaleString()}/yr
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-slate-500 text-xs">—</span>
                                )}
                              </dd>
                            </div>
                          </>
                        );
                      })()}
                    </dl>
                    {stateCode && (
                      <div
                        data-testid={`rate-mobile-state-pill-${r.slug}`}
                        className="-mt-2 mb-3 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] bg-[#10B981]/15 text-[#0A2540] border border-[#10B981]/30"
                      >
                        Avg in {stateCode}
                      </div>
                    )}
                    {r.editor_review && isOpen && (
                      <p
                        data-testid={`rate-mobile-editor-review-${r.slug}`}
                        className="text-[13px] text-slate-700 leading-relaxed mb-4"
                      >
                        {r.editor_review}
                      </p>
                    )}
                  </>
                ) : (
                  r.editor_review && isOpen && (
                    <p
                      data-testid={`rate-mobile-editor-review-${r.slug}`}
                      className="text-[13px] text-slate-700 leading-relaxed mb-4"
                    >
                      {r.editor_review}
                    </p>
                  )
                )}
                {/* Iter-150 — Mobile sticky CTA. The Apply button now
                    sticks to the bottom of the visible viewport (within
                    the article's vertical bounds) so it stays in reach
                    as the user scans long editor_review copy + score
                    breakdowns on small screens. `position: sticky` is
                    GPU-friendly and reduced-motion safe by nature (no
                    animation). On desktop (md:) the sticky behaviour is
                    disabled because the table layout already keeps the
                    CTA inline next to each row. */}
                <div className="sticky bottom-2 z-10 md:static md:bottom-auto md:z-auto bg-white/95 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none pt-2 md:pt-0 -mx-4 md:mx-0 px-4 md:px-0 border-t md:border-t-0 border-slate-100">
                  <ApplyButton
                    rate={r}
                    sourcePage={sourcePage}
                    config={config}
                    fullWidth
                    testIdSuffix="-mobile"
                    rankMeta={{
                      rank: showRank ? idx + 1 : null,
                      isEditorsPick,
                      sortMode: sortBy,
                    }}
                  />
                </div>
                <div className="mt-2 flex justify-center">
                  <CompareToggle rate={r} testIdSuffix="-mobile" />
                </div>
                {/* Iter-195.B (T5) — Mobile tap-to-expand affordance.
                    Renders a clean "Show details" or "Hide details"
                    button that toggles the same expandedSlug state
                    used by the rating chevron, so the visitor has
                    two affordances pointing at the same UX. */}
                <div className="mt-2 flex justify-center">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(r.slug)}
                    data-testid={`mobile-tap-expand-${r.slug}`}
                    aria-expanded={isOpen}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 hover:text-[#10B981] transition-colors py-1"
                  >
                    <ChevronDown
                      className={`w-3 h-3 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                      strokeWidth={2.5}
                    />
                    {isOpen ? "Hide details" : "Show details"}
                  </button>
                </div>
              </div>
              {isOpen && (
                <ScoreBreakdown
                  rate={r}
                  rank={showRank ? idx + 1 : null}
                  isEditorsPick={isEditorsPick}
                />
              )}
            </article>
          );
        })}
      </div>

      {/* Iter-185.AD — "See N more" toggle relocated here (was at top
          of mobile layout pre-185.AD) so it sits at the bottom of
          BOTH the desktop table and mobile cards layouts. User
          expects the expand action AFTER scanning the visible
          options, not before. */}
      {hiddenRowCount > 0 && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setExpandedLimit((n) => n + 5)}
            data-testid="lender-rate-see-more"
            className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 hover:border-slate-400 text-slate-950 font-bold h-10 px-5 text-xs uppercase tracking-[0.14em] transition-colors"
          >
            See {Math.min(5, hiddenRowCount)} more{" "}
            {category === "insurance"
              ? "carriers"
              : category === "warranty"
              ? "providers"
              : "lenders"}
            <span className="text-slate-500">
              ({sorted.length} of {rates.length} shown)
            </span>
          </button>
        </div>
      )}

      <p className="text-[11px] text-slate-500 leading-relaxed mt-4">
        {isCreditVertical ? (
          <>
            APR ranges are sourced from each lender&apos;s public site and are updated
            regularly. Your actual rate depends on credit history, loan amount, vehicle,
            and state. CarSavr may earn a commission when you apply through our links —
            it never affects how we rank lenders.
          </>
        ) : category === "warranty" ? (
          <>
            Warranty plan costs vary by vehicle make, model, mileage, and coverage tier.
            Quotes are provided directly by the provider. CarSavr may earn a commission
            when you purchase a plan through our links — it never affects how we rank
            providers.
          </>
        ) : (
          <>
            {/* Iter-183.2 — Insurance data-source citation. NerdWallet /
                Bankrate / Insure.com all carry an explicit source-and-
                sample-driver disclaimer at the bottom of their premium
                comparison tables. CarSavr matches that pattern for
                editorial transparency + affiliate-network compliance. */}
            <strong className="text-slate-700">Premium data:</strong>{" "}
            2024 national-average annual premiums published by Quadrant
            Information Services from state-DOI rate filings. Sample
            driver: 35-year-old · clean driving record · $100/$300/$100
            full coverage · $1,000 deductible · median ZIP code. Your
            actual quote will vary based on age, ZIP, driving record,
            vehicle, credit, and coverage selections. CarSavr may earn a
            commission when you buy a policy through our links — it
            never affects how we rank carriers.
          </>
        )}
      </p>

      {/* Iter-185.J — Provider trademark / logo disclaimer.
          Required when displaying third-party brand marks. The line
          is intentionally distinct from the affiliate disclaimer above
          so legal scope (commission relationship vs. trademark use) is
          easy to identify per FTC + USPTO best practices. Visible
          inline with every comparison surface so reviewers don't have
          to hunt for it. */}
      <p
        data-testid="lender-rate-trademark-disclaimer"
        className="mt-2 text-[11px] text-slate-500 leading-relaxed"
      >
        Provider logos and trademarks belong to their respective owners
        and are used for identification purposes only. Providers shown
        for comparison and educational purposes — display does not
        imply partnership unless an active affiliate relationship is
        stated separately.
      </p>

      {/* Iter-92 — Table sort methodology line. Briefly explains why rows
          are ordered the way they are by default, so users don't assume the
          top row is a paid placement. Links to the full /methodology page
          for the curious. Surfaces directly above any closing CTA so
          attribution context stays with the comparison data. */}
      <p
        data-testid="lender-rate-sort-explainer"
        className="mt-2 text-[11px] text-slate-500 leading-relaxed"
      >
        <span className="font-bold text-slate-700">How rows are ranked:</span>{" "}
        {(() => {
          // Iter-108 — Category-aware sort-rationale copy. The previous
          // iteration of this paragraph used "APR / value" as the
          // tie-break clause everywhere, which read as a loan-vertical
          // leak when surfaced on insurance / warranty index pages.
          // We now derive the secondary signal label from the
          // category so the copy stays accurate per vertical.
          //
          // Mapping:
          //   auto-loan / refinance → "APR / value"  (loan signal)
          //   insurance             → "premium / fit"
          //   warranty              → "plan value / fit"
          const secondarySignal = isCreditVertical
            ? "APR / value"
            : category === "warranty"
              ? "plan value / fit"
              : "premium / fit";
          if (sortBy === "featured") {
            return "Editor's pick first, then by overall rating. Promoted placements are flagged ";
          }
          if (sortBy === "lowest-apr") {
            return "Lowest published APR first, ties broken by editorial rating. Promoted placements remain flagged ";
          }
          if (sortBy === "highest-rated") {
            return `Highest overall rating first, ties broken by ${secondarySignal}. Promoted placements remain flagged `;
          }
          if (sortBy === "lowest-credit") {
            return "Lowest minimum credit score first, ties broken by editorial rating. Promoted placements remain flagged ";
          }
          return "Sorted by editorial rating. Promoted placements remain flagged ";
        })()}
        with a <span className="font-mono text-[10px] bg-slate-100 px-1 py-0.5">Sponsored</span> badge.{" "}
        <Link
          to="/methodology"
          className="font-semibold text-slate-700 hover:text-slate-950 underline-offset-2 hover:underline"
        >
          Read the full methodology →
        </Link>
      </p>
    </section>
  );
}

// Iter-148 — Unified table rank chip.
// Renders a compact, leaderboard-style "#1/#2/#3" badge in the lender
// cell on the top 3 rows of every comparison table when sorted by
// "Featured". The #1 row gets the saturated emerald + ink treatment
// ("Editor's Pick"); rows #2 and #3 get a quieter slate treatment so
// the eye lands on the editorial recommendation first.
//
// Used across:
//   - desktop table (credit-vertical + comparison-vertical layouts)
//   - mobile card layout
// Sharing one component keeps the visual language identical at every
// breakpoint, which is the iter-148 unification mandate.
function RankChip({ rank, isPick = false, slug }) {
  return (
    <span
      data-testid={`rank-chip-${slug}`}
      data-rank={rank}
      aria-label={isPick ? `Rank ${rank} — Editor's Pick` : `Rank ${rank}`}
      className={`shrink-0 inline-flex items-center justify-center w-7 h-7 font-display font-black text-[13px] tabular-nums leading-none ${
        isPick
          ? "bg-[#0A2540] text-[#10B981] ring-2 ring-[#10B981]/40"
          : "bg-slate-100 text-slate-500"
      }`}
    >
      {rank}
    </span>
  );
}

// Iter-66 Sprint C-3 — "Add to compare" toggle. Adds the lender row to the
// global compare stash so the user can stack 2–4 picks for the side-by-side
// drawer. Reflects current stash state and shows a green check when added.
//
// Iter-93 — First row also renders a one-time "Tap to stack 2–4 lenders"
// tooltip gated by localStorage so power-shoppers discover the feature
// without nagging returning visitors.
//
// Iter-407 / Iter-408 — All shared user-facing copy on this surface
// (compare-hint tooltip, "Comparing N {noun}" caption, verified-pair
// label, empty-state hint) lives in `./partnerRateTable/copy.js` so
// edits to one vertical's terminology don't risk leaking into another.
// See that file for the per-category maps + fallbacks.
function CompareToggle({ rate, testIdSuffix = "", showFirstScrollHint = false }) {
  const { isStashed, add, remove, items, maxStash } = useCompareStash();
  const stashed = isStashed(rate.slug);
  const atCap = items.length >= maxStash && !stashed;
  // First-scroll hint state (only the first row in the table opts in via prop).
  const [hintVisible, setHintVisible] = useState(false);
  useEffect(() => {
    if (!showFirstScrollHint || typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem(COMPARE_HINT_STORAGE_KEY)) return;
    } catch {
      return;
    }
    // Delay so the hint feels prompted by scroll, not page load.
    const t = setTimeout(() => setHintVisible(true), 1800);
    return () => clearTimeout(t);
  }, [showFirstScrollHint]);
  const dismissHint = () => {
    setHintVisible(false);
    try {
      window.localStorage.setItem(COMPARE_HINT_STORAGE_KEY, "1");
    } catch {
      /* private mode / quota — fail silently */
    }
  };
  const handle = () => {
    if (hintVisible) dismissHint();
    if (stashed) {
      remove(rate.slug);
      return;
    }
    if (atCap) return;
    add({
      slug: rate.slug,
      name: rate.name,
      category: rate.category,
      min_apr: rate.min_apr,
      max_apr: rate.max_apr,
      rating: rate.rating,
      min_credit: rate.min_credit,
      best_for: rate.highlight || rate.best_for || null,
      badge: rate.badge || rate.editor_pick || null,
      // Iter-94 — Carry the category-specific details dict into the stash so
      // the CompareDrawer modal can surface per-vertical fields (avg premium,
      // claim satisfaction, mileage cap, etc.) side-by-side — not just the
      // universal APR/credit/rating.
      details: rate.details || null,
    });
  };
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={handle}
        disabled={atCap}
        data-testid={`compare-toggle-${rate.slug}${testIdSuffix}`}
        aria-pressed={stashed}
        aria-label={stashed ? `Remove ${rate.name} from compare` : `Add ${rate.name} to compare`}
        // Iter-276.F (Top-20 #18 a11y) — WCAG 2.5.5 Target Size (Level
        // AAA) requires interactive controls to have at least 44×44 CSS
        // pixels of activation area. Pre-iter-276.F the compare-stash
        // chip rendered at ~16×72 px (text-[10px] + Plus icon), well
        // below the threshold. Added `min-h-[44px] min-w-[44px]` so
        // the tap target meets the standard. The negative `-my-2`
        // absorbs the extra vertical padding so the chip stays
        // visually compact inside the row's existing rhythm (the
        // wider hit area is invisible — only matters on touch input).
        // The `px-2 -mx-2` does the same on the X axis so the chip's
        // visible width doesn't push the Apply CTA off the row.
        className={`mt-1.5 -my-2 -mx-2 px-2 inline-flex items-center justify-center gap-1 min-h-[44px] min-w-[44px] text-[10px] font-bold uppercase tracking-[0.14em] transition-colors ${
          stashed
            ? "text-[#10B981] hover:text-rose-600"
            : atCap
              ? "text-slate-300 cursor-not-allowed"
              : "text-slate-500 hover:text-slate-950"
        }`}
      >
      {stashed ? (
        <>
          <Check className="w-3 h-3" /> Added
        </>
      ) : atCap ? (
        <>
          <Plus className="w-3 h-3" /> Stash full
        </>
      ) : (
        <>
          <Plus className="w-3 h-3" /> Compare
        </>
      )}
      </button>
      {hintVisible && (
        <span
          role="tooltip"
          data-testid="compare-first-scroll-hint"
          className="absolute right-0 top-full mt-2 w-[220px] z-20 bg-[#0A2540] text-white text-[11px] leading-snug font-medium px-3 py-2.5 shadow-xl normal-case tracking-normal animate-[fadeIn_0.3s_ease-out]"
        >
          <span aria-hidden className="absolute -top-1.5 right-3 w-3 h-3 bg-[#0A2540] rotate-45" />
          <span className="block font-bold text-[#10B981] uppercase tracking-[0.16em] text-[10px] mb-1">
            New
          </span>
          {getCompareHintCopy(rate.category)}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              dismissHint();
            }}
            aria-label="Dismiss compare hint"
            className="absolute top-1 right-1.5 text-slate-500 hover:text-white p-1"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      )}
    </span>
  );
}

function ApplyButton({ rate, sourcePage, config, fullWidth, testIdSuffix = "", rankMeta }) {
  const hasUrl = Boolean(rate.affiliate_url);
  const { open: openQuoteModal } = useQuoteModal();
  const cfg = config || getCategoryConfig(rate.category);

  // Pre-approval / paused-partner fallback: route the click to our Quote
  // Modal in the lender's category. We still record the click (with a
  // fallback target tag) and capture a first-party email lead instead of
  // showing a dead "Coming soon" button. Major conversion lift on pages
  // where no affiliate URLs are wired yet.
  //
  // Iter-150 — Same rank metadata captured on affiliate clicks is now
  // captured on fallback clicks too, so the funnel attribution dashboard
  // sees BOTH paths (affiliate handoff + first-party lead capture) and
  // can compute true Editor's Pick lift even on pages where the iter-128
  // partner pipeline hasn't shipped URLs yet.
  const handleFallbackClick = () => {
    recordClick({
      target: `lender-fallback:${rate.slug}`,
      source_page: sourcePage,
    });
    track(events.LENDER_FALLBACK_CLICK, {
      partner_name: rate.name,
      partner_slug: rate.slug,
      offer_type: rate.category,
      page_location: sourcePage,
      rank: rankMeta?.rank ?? null,
      is_editors_pick: rankMeta?.isEditorsPick === true,
      sort_mode: rankMeta?.sortMode || null,
    });
    const modalCategory = CATEGORY_TO_MODAL[rate.category] || "insurance";
    openQuoteModal(modalCategory);
  };

  return (
    <div
      className={`inline-flex flex-col items-${fullWidth ? "stretch" : "end"} gap-1.5 ${
        fullWidth ? "w-full" : ""
      }`}
    >
      <button
        type="button"
        onClick={
          hasUrl
            ? () => handleApplyClick(rate, sourcePage, rankMeta)
            : handleFallbackClick
        }
        data-testid={`rate-apply-${rate.slug}${testIdSuffix}`}
        data-rank={rankMeta?.rank ?? undefined}
        data-editors-pick={rankMeta?.isEditorsPick || undefined}
        // Iter-276.E (Top-20 #9) — "2-minute completion" microcopy.
        // Title attr surfaces the completion-time + soft-pull signal
        // on hover (desktop) + screen-reader users, without
        // re-introducing the iter-190.J per-row trust strip that
        // bloated the table visually. Pairs with the iter-276.A
        // "2-min compare" eyebrow already on the mobile Top Pick.
        // Iter-277.A (Audit P3 #20) — "What happens next" prefix:
        // hover tooltip now leads with WHERE the click lands so the
        // visitor doesn't doubt the navigation contract. Fallback
        // rows (no affiliate URL) get a "We'll match you to..." line
        // pointing at the QuoteModal first-party capture path.
        title={
          cfg.applyTooltip ||
          (hasUrl
            ? `Opens ${rate.name} in a new tab — your spot here is saved. ` +
              (cfg.category === "insurance" || rate.category === "insurance"
                ? "≈2-minute quote · soft pull only, won't impact your credit."
                : "≈2-minute pre-qualify · soft pull only, won't ding your credit.")
            : `We'll match you to a ${cfg.category === "insurance" || rate.category === "insurance" ? "carrier" : "lender"} that fits your profile — no spam, just one short form.`)
        }
        className={`inline-flex items-center justify-center gap-1.5 font-bold text-sm h-11 px-4 transition-all ${
          fullWidth ? "w-full" : ""
        } ${
          hasUrl
            ? "bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] hover:-translate-y-0.5"
            : "bg-[#10B981]/15 hover:bg-[#10B981] text-[#0A2540]"
        }`}
      >
        {cfg.ctaLabel}
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
      {/* Iter-276.E (Top-20 #9 + #15) — Completion-time microcopy.
          One compact line below the Apply CTA: "≈2 min · Soft pull".
          The Check icon paired with text gives the affordance a
          second (icon-shape) layer for color-blind viewers (audit
          #15 a11y win). Hidden when the row is the affiliate-less
          fallback (we route those to the QuoteModal instead, which
          has its own completion-time signal). */}
      {hasUrl && (
        <span
          data-testid={`rate-apply-trust-${rate.slug}${testIdSuffix}`}
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500"
        >
          <Check
            className="w-2.5 h-2.5 text-[#10B981]"
            strokeWidth={3}
            aria-hidden
          />
          ≈2 min · Soft pull
        </span>
      )}
      {hasUrl && <AffiliateBadge slug={`${rate.slug}${testIdSuffix}`} />}
    </div>
  );
}

// FTC-grade per-link disclosure used to render adjacent to every Apply
// button. Iter-276.G (Top-20 #20) — Extracted to
// `./partnerRateTable/AffiliateBadge.jsx`.

// Iter-63: compact filter dropdown used by the user-filter chips strip on
// listing tables. Each chip displays the field label inline and uses a
// native <select> so it inherits OS-level keyboard nav + screen-reader
// behavior for free.
//
// Iter-276.G (Top-20 #20) — Extracted to
// `./partnerRateTable/FilterSelect.jsx`. Module-level import lives at
// the top of this file; the inline definition is gone.
